import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { IconName } from '@vsolv/vectors-ui/icons';
import { TabsComponent } from '@vsolv/vectors-ui/tabs';
import { Asset } from '@wsphere/assets/domain';
import { AssetService } from '@wsphere/assets/web';
import { Distributor } from '@wsphere/distributors/domain';
import { SecurityService } from '@wsphere/staff/web';
import { ContractLedger, normalizePropertyValues, Policy } from '@wsphere/warranties/domain';
// eslint-disable-next-line @typescript-eslint/no-namespace
import { Conditions } from '@vsolv/packages/conditions/domain';
import { distinctUntilChanged, filter, firstValueFrom, Subject, switchMap, takeUntil } from 'rxjs';
import { PolicyService } from '../../services/policy.service';
import { escapeHtml } from '../../utils/escape-html.utils';
import {
  Cell,
  CellFormula,
  CellValueType,
  Dimension,
  Element,
  Paginated,
  PricingPreview,
  RatingMatrixService,
  RecipientReviewCell,
} from '../../services/rating-matrix.service';
import {
  buildConfigurationReport,
  ConfigurationReport,
  ConfigurationReportElement,
  ConfigurationReportRow,
  ConfigurationReportTable,
} from './rating-matrix-configuration-report';

type IntersectionFormula = NonNullable<PricingPreview['intersections'][number]['formula']>;

interface SelectedDimension {
  dimensionId: string;
  dimension: Dimension;
  elements: Element[];
  selectedElementIds?: string[]; // For filter dimensions - which elements are selected (array for multiselect)
}

interface MatrixRow {
  rowKey: string;
  rowLabels: string[];
  rowElementIds: string[];
}

interface MatrixColumn {
  colKey: string;
  colLabels: string[];
  colElementIds: string[];
}

/** Sentinel key used when a matrix axis has no dimensions (1D row-only or column-only). */
const EMPTY_AXIS_KEY = '';
const PAGE_FETCH_CONCURRENCY = 6;
const MAX_PAGES = 1000;

/** Columns can't scroll, so cap how wide and how deeply nested a jumped-to grid's header gets. */
const MAX_MATRIX_COLUMNS = 12;
const MAX_MATRIX_COL_DIMENSIONS = 2;

/** One dimension's placement while the layout dialog is open. */
interface MatrixLayoutEntry {
  dimensionId: string;
  title: string;
  /** How many elements the dimension has — drives the previewed grid size. */
  count: number;
  type: 'row' | 'col' | 'filter';
}

interface CellView {
  rowKey: string;
  colKey: string;
  filterElementIds: string[];
  allElementIds: string[];
  key: string;
  cell?: Cell;
  value?: number | null;
  valueType?: CellValueType;
  formula?: CellFormula | null;
  canUseFormula?: boolean;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
  dirty?: boolean;
}

interface OptionalCoverageOption {
  /**
   * Option id used in the UI select.
   * - For individual add-ons: coverageId
   * - For grouped add-ons: addonId
   */
  id: string;
  /**
   * Label shown to the user.
   * - For individual add-ons: coverage title
   * - For grouped add-ons: addon title (e.g. "Medium")
   */
  title: string;
  /**
   * Underlying coverage ids this option controls.
   * - For individual add-ons: single coverage id
   * - For grouped add-ons: all coverages in that group
   */
  coverageIds: string[];
  /**
   * True when this option represents a group of coverages tied to a plan addon.
   */
  isGroup: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-rating-matrix-page',
  templateUrl: './policy-rating-matrix.page.html',
  standalone: false,
})
export class PolicyRatingMatrixPage implements OnInit, OnDestroy {
  constructor(
    private ratingMatrixService: RatingMatrixService,
    private toastService: ToastService,
    private policyService: PolicyService,
    private assetService: AssetService,
    private securityService: SecurityService,
    private changeDetector: ChangeDetectorRef
  ) {}

  private destroy$ = new Subject<void>();
  private currentPlanId: string | null = null;
  private currentPolicyId: string | null = null;
  @ViewChild(TabsComponent) tabs?: TabsComponent;

  loading = false;
  saving = false;
  loadingReport = false;
  activeView: 'matrix' | 'report' = 'matrix';
  configurationReport: ConfigurationReport | null = null;

  /** Gates the matrix and report editors — read-only without it. */
  canEditMatrix = false;

  /** The report reads as a report until the user explicitly enters edit mode. */
  isEditingReport = false;

  /** Pending value edits on the report, keyed by cellId, until Save changes is pressed. */
  private reportRowDrafts = new Map<string, number | null>();
  /** Subset of the drafts that differ from the saved value — kept as a set so the template stays O(1). */
  private dirtyReportRowIds = new Set<string>();
  isSavingReport = false;
  deletingReportRow: ConfigurationReportRow | null = null;
  formulaDialogRow: ConfigurationReportRow | null = null;
  formulaDialogView: CellView | null = null;
  formulaDialogMode: CellValueType = 'FIXED';
  formulaDialogValue: number | null = null;
  formulaDialogPercent: number | null = null;
  formulaDialogFeeTypeIds = new Set<string>();
  isSavingFormula = false;
  deletingReportRowLabel = '';
  deletingReportRowValue = '';
  isRemovingReportRow = false;

  /** Working state for the layout dialog, seeded from the heuristic each time it opens. */
  matrixLayoutDraft: MatrixLayoutEntry[] = [];
  layoutDialogTitle = '';
  /** Table whose elements are still loading, so its button can show progress. */
  openingLayoutTableKey: string | null = null;

  dimensions: Dimension[] = [];
  allElements: Map<string, Element[]> = new Map();
  elementDimensionMap: Record<string, string> = {};

  selectedRowDimensions: SelectedDimension[] = [];
  selectedColDimensions: SelectedDimension[] = [];
  selectedFilterDimensions: SelectedDimension[] = [];

  cells: Cell[] = [];
  matrixRows: MatrixRow[] = [];
  matrixColumns: MatrixColumn[] = [];
  matrixCells: Map<string, CellView> = new Map();
  private displayedMatrixCells: Map<string, Map<string, CellView>> = new Map();

  /** Used by the template when resolving 1D cells on the missing row/column axis. */
  readonly emptyAxisKey = EMPTY_AXIS_KEY;

  selectedAssetId: string | null = null;
  assets: Asset.Model[] = [];
  assetValues: Record<string, unknown> = {};
  assetDimensionValues: Record<string, unknown> = {};
  loadingAssets = false;

  // Asset input modal
  showAssetPickerModal = false;
  selectedAsset: Asset.Model | null = null;
  asset = new FormControl<Asset.Model | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assetInfo = new FormControl<{ propertyValues: Record<string, any>; dimensionValues: Record<string, any> } | null>(
    null
  );
  propertySetId: string | null = null;

  // Term and addon selection for pricing preview
  selectedTermId: string | null = null;
  // Optional coverages the user can toggle on/off in the preview
  selectedOptionalCoverageIds: string[] = [];
  selectedAddonIds: string[] = [];
  termElements: Element[] = []; // Term dimension elements (synced from policy terms)
  /**
   * Optional coverages (neither BASE nor NOT_APPLICABLE for the current asset/term),
   * represented as options:
   * - Individual add-ons (per-coverage)
   * - Grouped add-ons (non "individual add-ons"), grouped by their plan addon name
   */
  availableCoverages: OptionalCoverageOption[] = [];
  /**
   * The ids of options currently selected in the UI select.
   * We derive selectedOptionalCoverageIds from these option selections.
   */
  selectedOptionalCoverageOptionIds: string[] = [];
  /**
   * Coverages whose evaluated requirement is BASE for the selected term/asset.
   * These are always included in the calculation and are not shown as selectable options.
   */
  alwaysIncludedCoverages: Array<{ id: string; title: string }> = [];
  planAddons: Policy.Plan.Addon[] = [];

  // Pricing dimension selection
  selectedPricingDimensionIds: string[] = [];
  pricingDimensions: Dimension[] = [];

  /**
   * Cached flag indicating whether any of the dimensions currently
   * participating in the matrix (rows, columns, filters) is marked
   * as a pricing dimension. This is used by the template to decide
   * whether boolean cell values are allowed, without recalculating
   * on every change-detection pass.
   */
  hasPricingDimensionInMatrix = false;

  pricingPreview: PricingPreview | null = null;
  loadingPricing = false;
  asOfDate: string | null = null;
  // Optional distributor used to scope the pricing preview.
  // Keep the full model (not just the id) so the picker keeps displaying the
  // selection after the component re-mounts (e.g. switching Matrix/Report tabs).
  selectedDistributor: Distributor.Model | null = null;
  selectedDistributorId: string | null = null;
  asOfDateModalValue: string | null = null;
  renderAsOfDatePicker = false;
  effectiveDateModalView: CellView | null = null;
  effectiveDateModalFrom: string | null = null;
  effectiveDateModalTo: string | null = null;
  // The cell's recipient override, edited alongside its dates; saved only when changed.
  effectiveDateModalRecipient: ContractLedger.RecipientAssignment | null = null;
  // Cells that would issue on the legacy fallback attribution — surfaced, never auto-assigned.
  recipientReviewCells: RecipientReviewCell[] = [];
  showRecipientReview = false;
  private effectiveDateModalInitialRecipientKey = '';
  effectiveDateModalMatrixCellKey: string | null = null;
  renderEffectiveDatePickers = false;
  private effectiveDateModalRenderToken = 0;
  private lastRefreshedMakeModelKey: string | null = null;
  private loadingAssetDimensionElementIds = new Set<string>();

  pricingExists(): boolean {
    return this.dimensions.some(d => d.isPricing);
  }

  hasAssetValues(): boolean {
    return (
      (this.assetValues && Object.keys(this.assetValues).length > 0) ||
      (this.assetDimensionValues && Object.keys(this.assetDimensionValues).length > 0)
    );
  }

  getAssetValueCount(): number {
    return this.assetValues ? Object.keys(this.assetValues).length : 0;
  }

  async ngOnInit() {
    // 'global' scope on the rating cell endpoints, so no permission key to scope against
    this.canEditMatrix = await this.securityService.hasAccess('wrt_EditRatingMatrix', null);

    this.policyService
      .getPolicyObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(policy => {
        this.currentPolicyId = policy?.id ?? null;
      });

    await Promise.all([this.loadDimensions(), this.loadAssets()]);

    // Load term elements and pricing dimensions after dimensions are loaded
    await this.loadTermsAndAddons();

    // Load cells for initial plan
    const initialPlan = await firstValueFrom(this.policyService.getPlanObservable());
    if (initialPlan?.id) {
      this.currentPlanId = initialPlan.id;
      await this.loadCells();
    }

    // Subscribe to plan changes and reload cells when plan changes
    this.policyService
      .getPlanObservable()
      .pipe(
        distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
        filter(plan => plan !== null && plan.id !== this.currentPlanId),
        switchMap(async plan => {
          if (plan?.id) {
            this.currentPlanId = plan.id;
            // Reset matrix state when plan changes
            this.selectedRowDimensions = [];
            this.selectedColDimensions = [];
            this.selectedFilterDimensions = [];
            this.updateHasPricingDimensionInMatrix();
            this.matrixRows = [];
            this.matrixColumns = [];
            this.matrixCells.clear();
            this.selectedTermId = null;
            this.selectedAddonIds = [];
            this.selectedOptionalCoverageIds = [];
            this.selectedOptionalCoverageOptionIds = [];
            this.availableCoverages = [];
            this.alwaysIncludedCoverages = [];
            this.selectedPricingDimensionIds = [];
            this.configurationReport = null;
            this.pricingPreview = null;
            this.activeView = 'matrix';
            this.tabs?.switchTo(0);
            // Load terms and addons for the new plan
            await this.loadTermsAndAddons();
            // Reload cells for the new plan
            await this.loadCells();
            // Recalculate pricing for the new plan if asset values are still present
            if (this.assetValues && Object.keys(this.assetValues).length > 0 && this.selectedTermId) {
              void this.calculatePricingPreview();
            }
          }
          return plan;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    // Terms and addons are already loaded in ngOnInit after dimensions
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadDimensions() {
    this.loading = true;
    try {
      const res = await this.ratingMatrixService.listDimensions({});
      if (!res) {
        this.dimensions = [];
        return;
      }
      this.dimensions = res.items;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.toastService.show({
        type: 'error',
        title: 'Failed to load dimensions',
        text: err?.message ?? 'Unable to load dimensions. Please try again.',
      });
    } finally {
      this.loading = false;
    }
  }

  private getMakeModelKeyFromAssetValues(): string | null {
    const makeModel = this.getMakeModelTrimFromAssetValues();
    if (!makeModel) return null;

    return `${makeModel.make.toLowerCase()}|${makeModel.model.toLowerCase()}|${(makeModel.trim ?? '').toLowerCase()}`;
  }

  private getMakeModelTrimFromAssetValues(): { make: string; model: string; trim: string | null } | null {
    const roadVehicle = this.assetValues['roadVehicle'];
    if (!roadVehicle || typeof roadVehicle !== 'object' || Array.isArray(roadVehicle)) return null;

    const vehicle = roadVehicle as Record<string, unknown>;
    const normalize = (value: unknown): string | null => {
      if (typeof value !== 'string') return null;
      const normalized = value.trim().replace(/\s+/g, ' ');
      return normalized || null;
    };

    const make = normalize(vehicle['roadVehicleMake']);
    const model = normalize(vehicle['roadVehicleModel']);
    const trim = normalize(vehicle['trim']);
    if (!make || !model) return null;

    return { make, model, trim };
  }

  private normalizeTitle(value: string): string {
    return value.trim().replace(/\s+/g, ' ').toLowerCase();
  }

  private findMakeModelDimension(): Dimension | null {
    return (
      this.dimensions.find(d => {
        const normalized = d.title.toLowerCase().replace(/[\s\-_/]+/g, '');
        return (
          normalized === 'makemodel' ||
          normalized === 'ymm' ||
          (normalized.includes('make') && normalized.includes('model'))
        );
      }) ?? null
    );
  }

  private async hasMakeModelEntry(make: string, model: string, trim?: string | null): Promise<boolean> {
    const makeModelDimension = this.findMakeModelDimension();
    if (!makeModelDimension) {
      return false;
    }

    const normalize = (v: string) => this.normalizeTitle(v);
    const makeResponse = await this.ratingMatrixService.listElements(makeModelDimension.id, {
      search: make,
      parentId: null,
    });

    const makeElement = (makeResponse?.items ?? []).find(element => normalize(element.title) === normalize(make));
    if (!makeElement) {
      return false;
    }

    const modelResponse = await this.ratingMatrixService.listElements(makeModelDimension.id, {
      search: model,
      parentId: makeElement.id,
    });

    const modelElement = (modelResponse?.items ?? []).find(element => normalize(element.title) === normalize(model));
    if (!modelElement) return false;

    if (!trim) return true;

    const trimResponse = await this.ratingMatrixService.listElements(makeModelDimension.id, {
      search: trim,
      parentId: modelElement.id,
    });

    return (trimResponse?.items ?? []).some(element => normalize(element.title) === normalize(trim));
  }

  private async refreshSelectedDimensionsAndMatrix(): Promise<void> {
    if (
      this.selectedRowDimensions.length === 0 &&
      this.selectedColDimensions.length === 0 &&
      this.selectedFilterDimensions.length === 0
    ) {
      await this.loadDimensions();
      return;
    }

    const previousFilterState = new Map<string, { selectedElementIds: string[]; hadAllSelected: boolean }>();
    this.selectedFilterDimensions.forEach(filterDim => {
      const selectedElementIds = [...(filterDim.selectedElementIds ?? [])];
      previousFilterState.set(filterDim.dimensionId, {
        selectedElementIds,
        hadAllSelected: selectedElementIds.length > 0 && selectedElementIds.length === filterDim.elements.length,
      });
    });

    await this.loadDimensions();

    const selectedIds = Array.from(
      new Set([
        ...this.selectedRowDimensions.map(d => d.dimensionId),
        ...this.selectedColDimensions.map(d => d.dimensionId),
        ...this.selectedFilterDimensions.map(d => d.dimensionId),
      ])
    );

    for (const dimensionId of selectedIds) {
      const selectedDimension =
        this.selectedRowDimensions.find(d => d.dimensionId === dimensionId) ||
        this.selectedColDimensions.find(d => d.dimensionId === dimensionId) ||
        this.selectedFilterDimensions.find(d => d.dimensionId === dimensionId);

      if (!selectedDimension) continue;

      const latestDimension = this.dimensions.find(d => d.id === dimensionId) ?? selectedDimension.dimension;
      const shouldScopeToPolicy =
        latestDimension.isSystem && (latestDimension.title === 'Coverage' || latestDimension.title === 'Term');

      const elements = await this.listAllElementsForDimension(dimensionId, {
        ...(shouldScopeToPolicy && this.currentPolicyId ? { policyId: this.currentPolicyId } : {}),
      });
      this.allElements.set(dimensionId, elements);
      elements.forEach(el => {
        this.elementDimensionMap[el.id] = el.dimensionId;
      });

      const applyUpdatedElements = (dim: SelectedDimension) => {
        dim.dimension = latestDimension;
        dim.elements = elements;
      };

      this.selectedRowDimensions.filter(d => d.dimensionId === dimensionId).forEach(applyUpdatedElements);
      this.selectedColDimensions.filter(d => d.dimensionId === dimensionId).forEach(applyUpdatedElements);

      this.selectedFilterDimensions
        .filter(d => d.dimensionId === dimensionId)
        .forEach(dim => {
          applyUpdatedElements(dim);

          const previous = previousFilterState.get(dimensionId);
          if (!previous) {
            dim.selectedElementIds = elements.map(e => e.id);
            return;
          }

          if (previous.hadAllSelected || previous.selectedElementIds.length === 0) {
            dim.selectedElementIds = elements.map(e => e.id);
            return;
          }

          const nextIds = new Set(elements.map(e => e.id));
          dim.selectedElementIds = previous.selectedElementIds.filter(id => nextIds.has(id));
        });
    }

    this.updateHasPricingDimensionInMatrix();
    this.buildMatrix();
  }

  async addDimensionAsRow(dimensionId: string) {
    if (this.selectedRowDimensions.some(d => d.dimensionId === dimensionId)) return;
    if (this.selectedColDimensions.some(d => d.dimensionId === dimensionId)) {
      this.toastService.show({
        type: 'error',
        title: 'Cannot add dimension',
        text: 'This dimension is already selected as a column. A dimension cannot be both a row and a column.',
      });
      return;
    }
    await this.addDimension(dimensionId, 'row');
  }

  async addDimensionAsColumn(dimensionId: string) {
    if (this.selectedColDimensions.some(d => d.dimensionId === dimensionId)) return;
    if (this.selectedRowDimensions.some(d => d.dimensionId === dimensionId)) {
      this.toastService.show({
        type: 'error',
        title: 'Cannot add dimension',
        text: 'This dimension is already selected as a row. A dimension cannot be both a row and a column.',
      });
      return;
    }
    await this.addDimension(dimensionId, 'col');
  }

  async addDimensionAsFilter(dimensionId: string) {
    if (this.selectedFilterDimensions.some(d => d.dimensionId === dimensionId)) return;
    await this.addDimension(dimensionId, 'filter');
  }

  private async addDimension(dimensionId: string, type: 'row' | 'col' | 'filter') {
    if (!(await this.stageDimension(dimensionId, type))) return;

    this.updateHasPricingDimensionInMatrix();
    await this.loadCells();
    this.buildMatrix();
  }

  /** Adds several dimensions at once, reloading cells and rebuilding the grid a single time. */
  private async addDimensions(specs: { dimensionId: string; type: 'row' | 'col' | 'filter' }[]): Promise<boolean> {
    let staged = false;
    for (const spec of specs) {
      if (await this.stageDimension(spec.dimensionId, spec.type)) staged = true;
    }

    if (!staged) return false;

    this.updateHasPricingDimensionInMatrix();
    await this.loadCells();
    this.buildMatrix();

    return true;
  }

  /** Loads and caches a dimension's elements. Null when the dimension or its elements are unavailable. */
  private async loadDimensionElements(dimensionId: string): Promise<Element[] | null> {
    const dimension = this.dimensions.find(d => d.id === dimensionId);
    if (!dimension) return null;

    const cached = this.allElements.get(dimensionId);
    if (cached) return cached;

    const shouldScopeToPolicy = dimension.isSystem && (dimension.title === 'Coverage' || dimension.title === 'Term');
    if (shouldScopeToPolicy && !this.currentPolicyId) {
      this.toastService.show({
        type: 'error',
        title: 'Policy not loaded',
        text: 'Load or select a policy before using Coverage or Term dimensions.',
      });
      return null;
    }

    try {
      const elements = await this.listAllElementsForDimension(dimensionId, {
        ...(shouldScopeToPolicy && this.currentPolicyId ? { policyId: this.currentPolicyId } : {}),
      });
      this.allElements.set(dimensionId, elements);
      elements.forEach(el => {
        this.elementDimensionMap[el.id] = el.dimensionId;
      });

      return elements;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.toastService.show({
        type: 'error',
        title: 'Failed to load elements',
        text: err?.message ?? 'Unable to load elements. Please try again.',
      });
      return null;
    }
  }

  /** Puts a dimension on an axis without reloading cells — the caller batches that. */
  private async stageDimension(dimensionId: string, type: 'row' | 'col' | 'filter'): Promise<boolean> {
    const dimension = this.dimensions.find(d => d.id === dimensionId);
    if (!dimension) return false;

    const elements = await this.loadDimensionElements(dimensionId);
    if (!elements) return false;

    const selected: SelectedDimension = { dimensionId, dimension, elements };
    if (type === 'row') {
      this.selectedRowDimensions.push(selected);
    } else if (type === 'col') {
      this.selectedColDimensions.push(selected);
    } else {
      // For filter dimensions, always initialize with all elements selected by default
      // This ensures that when a filter is removed and re-added, all elements are selected
      selected.selectedElementIds = elements.map(e => e.id);
      this.selectedFilterDimensions.push(selected);
    }

    return true;
  }

  removeRowDimension(index: number) {
    this.selectedRowDimensions.splice(index, 1);
    this.updateHasPricingDimensionInMatrix();
    this.buildMatrix();
  }

  removeColDimension(index: number) {
    this.selectedColDimensions.splice(index, 1);
    this.updateHasPricingDimensionInMatrix();
    this.buildMatrix();
  }

  removeFilterDimension(index: number) {
    this.selectedFilterDimensions.splice(index, 1);
    this.updateHasPricingDimensionInMatrix();
    this.buildMatrix();
  }

  moveRowDimensionUp(index: number) {
    if (index === 0) return;
    const temp = this.selectedRowDimensions[index];
    this.selectedRowDimensions[index] = this.selectedRowDimensions[index - 1];
    this.selectedRowDimensions[index - 1] = temp;
    this.buildMatrix();
  }

  moveRowDimensionDown(index: number) {
    if (index === this.selectedRowDimensions.length - 1) return;
    const temp = this.selectedRowDimensions[index];
    this.selectedRowDimensions[index] = this.selectedRowDimensions[index + 1];
    this.selectedRowDimensions[index + 1] = temp;
    this.buildMatrix();
  }

  moveColDimensionUp(index: number) {
    if (index === 0) return;
    const temp = this.selectedColDimensions[index];
    this.selectedColDimensions[index] = this.selectedColDimensions[index - 1];
    this.selectedColDimensions[index - 1] = temp;
    this.buildMatrix();
  }

  moveColDimensionDown(index: number) {
    if (index === this.selectedColDimensions.length - 1) return;
    const temp = this.selectedColDimensions[index];
    this.selectedColDimensions[index] = this.selectedColDimensions[index + 1];
    this.selectedColDimensions[index + 1] = temp;
    this.buildMatrix();
  }

  private async loadCells() {
    this.loading = true;
    try {
      this.cells = await this.fetchAllCellsForPlan();
      await this.loadFeeTypeElementsForFormulas();
      void this.refreshRecipientReview();
      if (this.activeView === 'report') {
        await this.refreshConfigurationReport();
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.toastService.show({
        type: 'error',
        title: 'Failed to load cells',
        text: err?.message ?? 'Unable to load cells. Please try again.',
      });
    } finally {
      this.loading = false;
    }
  }

  async onViewChange(viewIndex: number) {
    this.activeView = viewIndex === 1 ? 'report' : 'matrix';
    // Leaving the report drops any half-finished editing rather than holding it in the background
    this.exitReportEdit();

    if (this.activeView === 'report') {
      await this.refreshConfigurationReport();
    }
  }

  getReportElementLabel(element: ConfigurationReportElement): string {
    const source = element.sourceElement;
    if (!source) return element.elementTitle;

    // Report rows carry their own parent chain, so nested elements resolve even on a cold load
    // where the matrix never populated this dimension's elements
    const dimension = this.dimensions.find(d => d.id === element.dimensionId);
    const isNested = !!source.parent || !!source.parentId || (!!dimension && this.isHierarchicalDimension(dimension));

    return isNested ? this.buildElementHierarchyLabel(source) : element.elementTitle;
  }

  formatReportValue(value: number): string {
    if (!Number.isFinite(value)) {
      return '—';
    }

    if (Number.isInteger(value)) {
      return String(value);
    }

    return String(value);
  }

  getReportValueWithEffectiveDates(row: ConfigurationReportRow): string {
    const valueText =
      row.valueType === 'FORMULA' ? this.getFormulaLabel(row.formula) : this.formatReportValue(row.value);
    const normalizedFrom = this.normalizeDateInput(row.effectiveFrom);
    const normalizedTo = this.normalizeDateInput(row.effectiveTo);

    if (!normalizedFrom && !normalizedTo) {
      return valueText;
    }

    const from = this.formatDateForDisplay(normalizedFrom);
    const to = this.formatDateForDisplay(normalizedTo);
    return `${valueText} (${from} - ${to})`;
  }

  getReportDepthLabel(depth: number): string {
    return `${depth}-Dimensional Configuration`;
  }

  getReportEffectiveWindowLabel(row: ConfigurationReportRow): string {
    const normalizedFrom = this.normalizeDateInput(row.effectiveFrom);
    const normalizedTo = this.normalizeDateInput(row.effectiveTo);
    if (!normalizedFrom && !normalizedTo) return '';

    return `${this.formatDateForDisplay(normalizedFrom)} - ${this.formatDateForDisplay(normalizedTo)}`;
  }

  getReportRowIntersectionLabel(row: ConfigurationReportRow): string {
    const label = row.elements.map(element => this.getReportElementLabel(element)).join(' / ');
    return label || 'This value';
  }

  getReportDimensionColumnWidth(table: ConfigurationReportTable): number {
    return 78 / Math.max(table.headers.length, 1);
  }

  getReportTableLabel(table: ConfigurationReportTable): string {
    return table.headers.join(' × ');
  }

  /**
   * Chooses which dimensions go on which axis. Rows scroll and columns don't, so the widest
   * dimension anchors the rows and the narrowest fill the columns — but only while their product
   * stays readable. Anything that would overflow the header goes back onto the rows.
   */
  private async planMatrixAxes(dimensionIds: string[]): Promise<MatrixLayoutEntry[]> {
    // Uncached dimensions each cost a paginated fetch, so load them together rather than in turn
    const loaded = await Promise.all(
      [...new Set(dimensionIds)].map(async dimensionId => ({
        dimensionId,
        elements: await this.loadDimensionElements(dimensionId),
      }))
    );

    const sized = loaded
      .filter((entry): entry is { dimensionId: string; elements: Element[] } => !!entry.elements)
      .map(entry => ({ dimensionId: entry.dimensionId, count: Math.max(entry.elements.length, 1) }));

    if (!sized.length) return [];

    sized.sort((left, right) => right.count - left.count);

    const [widest, ...rest] = sized;
    const rows = [widest];
    const cols: typeof sized = [];

    // Smallest first, so the cheapest dimensions claim the column budget
    let columnProduct = 1;
    for (const entry of rest.reverse()) {
      const fits = cols.length < MAX_MATRIX_COL_DIMENSIONS && columnProduct * entry.count <= MAX_MATRIX_COLUMNS;

      if (fits) {
        columnProduct *= entry.count;
        cols.push(entry);
      } else {
        rows.push(entry);
      }
    }

    // The loop above visits smallest-first only to fit the budget; that's no reason to nest the
    // axes that way. Both axes read widest-outermost, matching the order dimensions were ranked in.
    const byWidest = (left: { count: number }, right: { count: number }) => right.count - left.count;
    rows.sort(byWidest);
    cols.sort(byWidest);

    const toEntry = (entry: { dimensionId: string; count: number }, type: 'row' | 'col'): MatrixLayoutEntry => ({
      dimensionId: entry.dimensionId,
      count: entry.count,
      title: this.dimensions.find(d => d.id === entry.dimensionId)?.title ?? entry.dimensionId,
      type,
    });

    return [...rows.map(entry => toEntry(entry, 'row')), ...cols.map(entry => toEntry(entry, 'col'))];
  }

  // --- Layout dialog -------------------------------------------------------

  /** Opens the layout chooser for a report table, pre-filled with the heuristic's suggestion. */
  async openReportTableLayout(table: ConfigurationReportTable, dialog: { open: () => void }) {
    // One dialog and one draft are shared by every table, so only one open can be in flight.
    // The template disables every button meanwhile, so this guard should never actually trip.
    if (!table.dimensionIds.length || this.openingLayoutTableKey) return;

    // A report built before a dimension was deleted still lists it. Placing only the survivors
    // would open a coarser intersection than the row that was clicked, so refuse and say which.
    const missing = this.getMissingReportDimensions(table);
    if (missing.length) {
      this.toastService.show({
        type: 'error',
        title: 'Unable to open in matrix',
        text: `${missing.join(', ')} no longer ${missing.length === 1 ? 'exists' : 'exist'}. Reload the plan to refresh this report.`,
      });
      return;
    }

    // Elements may not be cached yet, so the click can hang on a fetch — mark the button busy
    this.openingLayoutTableKey = table.dimensionIds.join('|');
    try {
      this.matrixLayoutDraft = await this.planMatrixAxes(table.dimensionIds);
    } finally {
      this.openingLayoutTableKey = null;
    }

    if (!this.matrixLayoutDraft.length) {
      this.toastService.show({
        type: 'error',
        title: 'Unable to open in matrix',
        text: 'Could not load the dimensions for this combination. Please try again.',
      });
      return;
    }

    this.layoutDialogTitle = this.getReportTableLabel(table);
    dialog.open();
  }

  /** Titles of the table's dimensions the plan no longer has. Headers line up with dimensionIds. */
  private getMissingReportDimensions(table: ConfigurationReportTable): string[] {
    return table.dimensionIds
      .map((dimensionId, index) => ({ dimensionId, title: table.headers[index] ?? dimensionId }))
      .filter(entry => !this.dimensions.some(dimension => dimension.id === entry.dimensionId))
      .map(entry => entry.title);
  }

  isOpeningLayout(table: ConfigurationReportTable): boolean {
    return this.openingLayoutTableKey === table.dimensionIds.join('|');
  }

  closeReportTableLayout(dialog?: { close: () => void }) {
    dialog?.close();
    this.matrixLayoutDraft = [];
    this.layoutDialogTitle = '';
  }

  setLayoutPlacement(entry: MatrixLayoutEntry, type: 'row' | 'col' | 'filter') {
    entry.type = type;
  }

  getLayoutIcon(type: 'row' | 'col' | 'filter'): IconName {
    if (type === 'row') return 'rows-01';
    return type === 'col' ? 'columns-01' : 'filter-lines';
  }

  /** Dimension names on one axis, in the order the matrix will nest them. */
  getLayoutNames(type: 'row' | 'col' | 'filter'): string {
    return this.matrixLayoutDraft
      .filter(entry => entry.type === type)
      .map(entry => entry.title)
      .join(' › ');
  }

  /** Grid height — filters don't multiply out, they pick one slice. */
  get layoutRowTotal(): number {
    return this.matrixLayoutDraft
      .filter(entry => entry.type === 'row')
      .reduce((total, entry) => total * entry.count, 1);
  }

  get layoutColTotal(): number {
    return this.matrixLayoutDraft
      .filter(entry => entry.type === 'col')
      .reduce((total, entry) => total * entry.count, 1);
  }

  /** Wide grids are the failure mode worth warning about — columns can't scroll away. */
  get isLayoutColumnHeavy(): boolean {
    return this.layoutColTotal > MAX_MATRIX_COLUMNS;
  }

  get canApplyLayout(): boolean {
    return this.matrixLayoutDraft.some(entry => entry.type === 'row' || entry.type === 'col');
  }

  async applyReportTableLayout(dialog?: { close: () => void }) {
    const specs = this.matrixLayoutDraft.map(entry => ({ dimensionId: entry.dimensionId, type: entry.type }));
    if (!specs.length) return;

    this.closeReportTableLayout(dialog);

    // Start clean, or a second jump collides with the axes the first one left behind
    this.selectedRowDimensions = [];
    this.selectedColDimensions = [];
    this.selectedFilterDimensions = [];

    // addDimensions rebuilds on success; it bails early when nothing staged, and the cleared
    // axes still need a rebuild so the old grid doesn't linger
    if (!(await this.addDimensions(specs))) this.buildMatrix();

    this.activeView = 'matrix';
    this.tabs?.switchTo(0);
  }

  /** Inputs and remove buttons only render once edit mode is entered. */
  get isReportEditable(): boolean {
    return this.canEditMatrix && this.isEditingReport;
  }

  get reportValueColumnWidth(): number {
    return this.isReportEditable ? 14 : 22;
  }

  readonly reportActionsColumnWidth = 8;

  startReportEdit() {
    this.isEditingReport = true;
  }

  exitReportEdit() {
    this.isEditingReport = false;
    this.cancelReportChanges();
  }

  getReportRowValue(row: ConfigurationReportRow): number | null {
    const draft = this.reportRowDrafts.get(row.cellId);
    return draft === undefined ? row.value : draft;
  }

  isReportRowDirty(row: ConfigurationReportRow): boolean {
    return this.dirtyReportRowIds.has(row.cellId);
  }

  get hasReportChanges(): boolean {
    return this.dirtyReportRowIds.size > 0;
  }

  onReportValueChange(row: ConfigurationReportRow, value: number | string | null) {
    const parsed = value === null || value === '' ? null : Number(value);
    // A cleared or non-finite input isn't a value — store null so NaN can never be read back out
    const next = parsed !== null && Number.isFinite(parsed) ? parsed : null;
    this.reportRowDrafts.set(row.cellId, next);

    if (next !== null && next !== row.value) {
      this.dirtyReportRowIds.add(row.cellId);
    } else {
      this.dirtyReportRowIds.delete(row.cellId);
    }
  }

  cancelReportChanges() {
    this.reportRowDrafts.clear();
    this.dirtyReportRowIds.clear();
  }

  /** Applies every pending report edit, then reloads once so the page reflects what actually landed. */
  async saveReportChanges() {
    if (!this.hasReportChanges || this.isSavingReport) return;

    const edits = this.getReportRows()
      .filter(row => this.dirtyReportRowIds.has(row.cellId))
      .map(row => ({ row, value: this.reportRowDrafts.get(row.cellId) as number }));

    this.isSavingReport = true;
    const applied: string[] = [];
    let failure: { label: string; err: unknown } | null = null;

    try {
      for (const edit of edits) {
        try {
          // planId is deliberately omitted — the cell already belongs to a plan, and the API
          // rejects a mismatched one as not found
          await this.ratingMatrixService.updateCell(edit.row.cellId, { value: edit.value });
          applied.push(edit.row.cellId);
        } catch (err: unknown) {
          failure = { label: this.getReportRowIntersectionLabel(edit.row), err };
          break;
        }
      }

      // Only what landed stops being pending — the rest stays put so a failure can be retried
      // without retyping, and so the saved rows aren't sent a second time
      for (const cellId of applied) {
        this.reportRowDrafts.delete(cellId);
        this.dirtyReportRowIds.delete(cellId);
      }

      await this.loadCells();
      this.buildMatrix();

      if (failure) {
        this.toastService.show({
          type: 'error',
          title: 'Failed to save changes',
          text: `${failure.label}: ${this.getRequestErrorMessage(failure.err, 'Unable to save this value.')}`,
        });
        return;
      }

      // Back to a read-only report once everything landed; a failure keeps edit mode open to retry
      this.isEditingReport = false;
      this.toastService.show({
        type: 'success',
        title: 'Changes saved',
        text:
          edits.length === 1
            ? `${this.getReportRowIntersectionLabel(edits[0].row)} has been updated.`
            : `${edits.length} values have been updated.`,
      });
    } finally {
      this.isSavingReport = false;
    }
  }

  private getReportRows(): ConfigurationReportRow[] {
    return (this.configurationReport?.sections ?? []).flatMap(section => section.tables.flatMap(table => table.rows));
  }

  /** Drops drafts whose row no longer exists — a removed row or a plan change. */
  private pruneReportDrafts() {
    const cellIds = new Set(this.getReportRows().map(row => row.cellId));

    for (const cellId of Array.from(this.reportRowDrafts.keys())) {
      if (cellIds.has(cellId)) continue;

      this.reportRowDrafts.delete(cellId);
      this.dirtyReportRowIds.delete(cellId);
    }
  }

  openDeleteReportRowModal(row: ConfigurationReportRow, dialog: { open: () => void }) {
    this.deletingReportRow = row;
    this.deletingReportRowLabel = this.getReportRowIntersectionLabel(row);
    this.deletingReportRowValue = this.getReportValueWithEffectiveDates(row);
    this.isRemovingReportRow = false;
    dialog.open();
  }

  closeDeleteReportRowModal(dialog?: { close: () => void }) {
    dialog?.close();
    this.deletingReportRow = null;
    this.deletingReportRowLabel = '';
    this.deletingReportRowValue = '';
    this.isRemovingReportRow = false;
  }

  async confirmDeleteReportRow(dialog?: { close: () => void }) {
    const row = this.deletingReportRow;
    if (!row || this.isRemovingReportRow) return;

    const label = this.deletingReportRowLabel;
    this.isRemovingReportRow = true;

    try {
      await this.ratingMatrixService.deleteCell(row.cellId);
      this.closeDeleteReportRowModal(dialog);
      await this.loadCells();
      this.buildMatrix();
      this.toastService.show({
        type: 'success',
        title: 'Value removed',
        text: `${label} has been removed from the rating matrix.`,
      });
    } catch (err: unknown) {
      this.isRemovingReportRow = false;
      this.toastService.show({
        type: 'error',
        title: 'Failed to remove value',
        text: this.getRequestErrorMessage(err, 'Unable to remove this value. Please try again.'),
      });
    }
  }

  /** The system Fee Type dimension. A formula's selectable charge types are its elements. */
  get feeTypeDimension(): Dimension | undefined {
    return this.dimensions.find(
      dimension => dimension.isSystem && dimension.title.toLowerCase().replace(/[\s\-_/]+/g, '') === 'feetype'
    );
  }

  canUseFormulaForRow(row: ConfigurationReportRow): boolean {
    return this.hasSellerAndFeeType(row.elements.map(element => element.dimensionId));
  }

  /** A percentage needs a Seller and a Fee Type in its cell, the same rule the API enforces on save. */
  private hasSellerAndFeeType(dimensionIds: string[]): boolean {
    const titles = this.dimensions
      .filter(dimension => dimension.isSystem && dimensionIds.includes(dimension.id))
      .map(dimension => dimension.title.toLowerCase().replace(/[\s\-_/]+/g, ''));
    return titles.includes('seller') && titles.includes('feetype');
  }

  /** e.g. "50%". */
  getFormulaLabel(formula: CellFormula | null | undefined): string {
    return formula ? `${formula.percent}%` : '—';
  }

  getFormulaChargeTitles(formula: CellFormula | null | undefined): string[] {
    return (formula?.feeTypeElementIds ?? []).map(id => this.getElementById(id)?.title ?? 'Unknown charge');
  }

  /** Formula labels name their charge types, so their titles must be cached before rendering. */
  private async loadFeeTypeElementsForFormulas() {
    const dimension = this.feeTypeDimension;
    if (dimension && this.cells.some(cell => cell.valueType === 'FORMULA')) {
      await this.loadDimensionElements(dimension.id);
    }
  }

  get formulaFeeTypeOptions(): Element[] {
    const dimensionId = this.feeTypeDimension?.id;
    return dimensionId ? (this.allElements.get(dimensionId) ?? []) : [];
  }

  get canSaveFormulaDialog(): boolean {
    if (this.isSavingFormula) return false;
    if (this.formulaDialogMode === 'FIXED') return Number.isFinite(this.formulaDialogValue);
    return Number.isFinite(this.formulaDialogPercent) && this.formulaDialogFeeTypeIds.size > 0;
  }

  get formulaDialogLabel(): string {
    if (this.formulaDialogRow) return this.getReportRowIntersectionLabel(this.formulaDialogRow);
    return (this.formulaDialogView?.allElementIds ?? []).map(id => this.getElementById(id)?.title ?? id).join(' + ');
  }

  async openFormulaDialog(row: ConfigurationReportRow, dialog: { open: () => void }) {
    await this.prepareFormulaDialog(row.valueType, row.value, row.formula);
    this.formulaDialogRow = row;
    dialog.open();
  }

  /** From the grid: the result is written into the cell and saved with the grid's other edits. */
  async openFormulaDialogForView(view: CellView, dialog: { open: () => void }) {
    await this.prepareFormulaDialog(view.valueType ?? 'FIXED', view.value ?? null, view.formula ?? null);
    this.formulaDialogView = view;
    dialog.open();
  }

  private async prepareFormulaDialog(valueType: CellValueType, value: number | null, formula: CellFormula | null) {
    const dimension = this.feeTypeDimension;
    if (dimension) await this.loadDimensionElements(dimension.id);

    this.formulaDialogMode = valueType;
    this.formulaDialogValue = valueType === 'FIXED' ? value : null;
    this.formulaDialogPercent = formula?.percent ?? null;
    this.formulaDialogFeeTypeIds = new Set(formula?.feeTypeElementIds ?? []);
    this.isSavingFormula = false;
  }

  closeFormulaDialog(dialog?: { close: () => void }) {
    dialog?.close();
    this.formulaDialogRow = null;
    this.formulaDialogView = null;
    this.formulaDialogFeeTypeIds = new Set();
    this.isSavingFormula = false;
  }

  toggleFormulaFeeType(elementId: string, isChecked: boolean) {
    const next = new Set(this.formulaDialogFeeTypeIds);
    if (isChecked) next.add(elementId);
    else next.delete(elementId);
    this.formulaDialogFeeTypeIds = next;
  }

  async saveFormulaDialog(dialog?: { close: () => void }) {
    if (!this.canSaveFormulaDialog) return;

    const view = this.formulaDialogView;
    if (view) {
      if (this.formulaDialogMode === 'FORMULA') {
        view.valueType = 'FORMULA';
        view.formula = {
          percent: Number(this.formulaDialogPercent),
          feeTypeElementIds: Array.from(this.formulaDialogFeeTypeIds),
        };
      } else {
        view.valueType = 'FIXED';
        view.formula = null;
        view.value = Number(this.formulaDialogValue);
      }
      view.dirty = true;
      this.closeFormulaDialog(dialog);
      await this.saveChanges();
      return;
    }

    const row = this.formulaDialogRow;
    if (!row) return;

    const label = this.getReportRowIntersectionLabel(row);
    this.isSavingFormula = true;

    try {
      await this.ratingMatrixService.updateCell(
        row.cellId,
        this.formulaDialogMode === 'FORMULA'
          ? {
              valueType: 'FORMULA',
              formula: {
                percent: Number(this.formulaDialogPercent),
                feeTypeElementIds: Array.from(this.formulaDialogFeeTypeIds),
              },
            }
          : { valueType: 'FIXED', value: Number(this.formulaDialogValue) }
      );
      this.closeFormulaDialog(dialog);
      // A pending numeric draft for this row is stale now
      this.reportRowDrafts.delete(row.cellId);
      this.dirtyReportRowIds.delete(row.cellId);
      await this.loadCells();
      this.buildMatrix();
      this.toastService.show({ type: 'success', title: 'Value saved', text: `${label} has been updated.` });
    } catch (err: unknown) {
      this.isSavingFormula = false;
      this.toastService.show({
        type: 'error',
        title: 'Failed to save value',
        text: this.getRequestErrorMessage(err, 'Unable to save this value. Please try again.'),
      });
    }
  }

  /** HttpErrorResponse.message is the generic transport string — the API's reason is on `error.message`. */
  private getRequestErrorMessage(err: unknown, fallback: string): string {
    const error = err as { error?: { message?: string }; message?: string };
    return error?.error?.message ?? error?.message ?? fallback;
  }

  private getElementById(elementId: string): Element | undefined {
    if (this.elementDimensionMap[elementId]) {
      const dimensionId = this.elementDimensionMap[elementId];
      const dimensionElements = this.allElements.get(dimensionId) ?? [];
      const cached = dimensionElements.find(element => element.id === elementId);
      if (cached) {
        return cached;
      }
    }

    for (const dimensionElements of this.allElements.values()) {
      const cached = dimensionElements.find(element => element.id === elementId);
      if (cached) {
        return cached;
      }
    }

    return undefined;
  }

  private getDimensionElementById(dimensionId: string, elementId: string): Element | undefined {
    const dimensionElements = this.allElements.get(dimensionId) ?? [];
    const directMatch = dimensionElements.find(element => element.id === elementId);
    if (directMatch) {
      return directMatch;
    }

    const fallback = this.getElementById(elementId);
    if (fallback?.dimensionId === dimensionId) {
      return fallback;
    }

    return undefined;
  }

  private getAssetEntryDisplayValue(key: string, value: unknown): unknown {
    if (value === null || value === undefined || value === '') {
      return value;
    }

    const rawKey = key.trim();
    const dimensionIdFromPrefixedKey = rawKey.match(/^dim\s+([a-zA-Z0-9_-]+)$/i)?.[1] ?? null;
    const dimensionIdCandidate = dimensionIdFromPrefixedKey ?? rawKey;
    const dimension = this.dimensions.find(d => d.id === dimensionIdCandidate);

    if (!dimension) {
      return value;
    }

    const toDisplayLabel = (candidate: unknown): unknown => {
      if (typeof candidate !== 'string' || candidate.length === 0) {
        return candidate;
      }

      const element = this.getDimensionElementById(dimension.id, candidate);
      if (!element) {
        return candidate;
      }

      if (this.isHierarchicalDimension(dimension)) {
        return this.buildElementHierarchyLabel(element);
      }

      return element.title;
    };

    if (Array.isArray(value)) {
      return value.map(item => toDisplayLabel(item));
    }

    return toDisplayLabel(value);
  }

  private formatAssetEntryValue(value: unknown): string | number {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    if (typeof value === 'number') {
      return value;
    }

    if (Array.isArray(value)) {
      return value
        .map(item => this.formatAssetEntryValue(item))
        .filter(item => item !== '')
        .join(', ');
    }

    if (typeof value === 'object') {
      if (this.isAddressLike(value)) {
        return this.formatAddressValue(value);
      }

      const formattedEntries = Object.entries(value)
        .map(([nestedKey, nestedValue]) => {
          const formattedValue = this.formatAssetEntryValue(nestedValue);
          if (formattedValue === '') {
            return null;
          }

          return `${this.getAssetEntryLabel(nestedKey)}: ${formattedValue}`;
        })
        .filter((entry): entry is string => entry !== null);

      return formattedEntries.join('; ');
    }

    return `${value}`;
  }

  private isAddressLike(value: object): value is {
    line1?: unknown;
    line2?: unknown;
    city?: unknown;
    state?: unknown;
    zip?: unknown;
    country?: unknown;
  } {
    return 'line1' in value && 'city' in value;
  }

  private formatAddressValue(address: {
    line1?: unknown;
    line2?: unknown;
    city?: unknown;
    state?: unknown;
    zip?: unknown;
    country?: unknown;
  }): string {
    const isPresent = (part: unknown) => part !== null && part !== undefined && part !== '';
    const locality = [address.city, address.state, address.zip].filter(isPresent).join(' ');

    return [address.line1, address.line2, locality, address.country].filter(isPresent).join(', ');
  }

  private async preloadAssetDimensionElements(dimensionValues: Record<string, unknown>): Promise<void> {
    const dimensionIds = Object.keys(dimensionValues ?? {})
      .map(rawKey => {
        const normalized = rawKey.trim();
        return normalized.match(/^dim\s+([a-zA-Z0-9_-]+)$/i)?.[1] ?? normalized;
      })
      .filter((dimensionId, index, all) => all.indexOf(dimensionId) === index)
      .filter(dimensionId => {
        const dimension = this.dimensions.find(candidate => candidate.id === dimensionId);
        if (!dimension) {
          return false;
        }

        if (this.allElements.has(dimensionId)) {
          return false;
        }

        if (this.loadingAssetDimensionElementIds.has(dimensionId)) {
          return false;
        }

        return true;
      });

    if (dimensionIds.length === 0) {
      return;
    }

    await Promise.all(
      dimensionIds.map(async dimensionId => {
        this.loadingAssetDimensionElementIds.add(dimensionId);
        try {
          const dimension = this.dimensions.find(candidate => candidate.id === dimensionId);
          if (!dimension) {
            return;
          }

          const shouldScopeToPolicy =
            dimension.isSystem && (dimension.title === 'Coverage' || dimension.title === 'Term');
          const elements = await this.listAllElementsForDimension(dimensionId, {
            ...(shouldScopeToPolicy && this.currentPolicyId ? { policyId: this.currentPolicyId } : {}),
          });
          this.allElements.set(dimensionId, elements);
          elements.forEach(element => {
            this.elementDimensionMap[element.id] = element.dimensionId;
          });
        } catch (error) {
          console.warn('Failed to preload asset dimension elements', { dimensionId, error });
        } finally {
          this.loadingAssetDimensionElementIds.delete(dimensionId);
        }
      })
    );
  }

  async refreshRecipientReview() {
    try {
      const plan = await firstValueFrom(this.policyService.getPlanObservable());
      this.recipientReviewCells = plan?.id ? await this.ratingMatrixService.listRecipientReviewCells(plan.id) : [];
    } catch {
      // Advisory only — never block the matrix on it.
      this.recipientReviewCells = [];
    }
  }

  describeRecipientReview(item: RecipientReviewCell): string {
    const cell = this.cells.find(c => c.id === item.cellId);
    const label = (cell?.elements ?? []).map(ce => ce.element?.title ?? ce.elementId).join(' + ') || item.cellId;
    const reason =
      item.reason === 'NO_FEE_TYPE'
        ? 'no Fee Type'
        : item.reason === 'UNRESOLVABLE'
          ? 'configured party no longer resolves'
          : 'no recipient';
    return `${label} — ${reason}`;
  }

  private async fetchAllCellsForPlan(): Promise<Cell[]> {
    const plan = await firstValueFrom(this.policyService.getPlanObservable());
    if (!plan?.id) {
      return [];
    }

    return this.fetchAllPages((page, limit) => this.ratingMatrixService.listCells({ planId: plan.id, page, limit }));
  }

  private async listAllElementsForDimension(
    dimensionId: string,
    params?: { policyId?: string; search?: string; parentId?: string | null; limit?: number }
  ): Promise<Element[]> {
    const limit = params?.limit ?? 100;
    return this.fetchAllPages(
      (page, pageLimit) =>
        this.ratingMatrixService.listElements(dimensionId, {
          ...params,
          page,
          limit: pageLimit,
        }),
      limit
    );
  }

  private async fetchAllPages<T>(
    fetchPage: (page: number, limit: number) => Promise<Paginated<T>>,
    limit = 100
  ): Promise<T[]> {
    const firstPage = await fetchPage(1, limit);
    const items = [...(firstPage?.items ?? [])];
    const totalItems = firstPage?.meta?.totalItems;

    if (typeof totalItems === 'number') {
      const totalPages = Math.min(Math.ceil(totalItems / limit), MAX_PAGES);

      for (let firstPageNumber = 2; firstPageNumber <= totalPages; firstPageNumber += PAGE_FETCH_CONCURRENCY) {
        const pageNumbers = Array.from(
          { length: Math.min(PAGE_FETCH_CONCURRENCY, totalPages - firstPageNumber + 1) },
          (_, index) => firstPageNumber + index
        );
        const pages = await Promise.all(pageNumbers.map(page => fetchPage(page, limit)));
        pages.forEach(page => items.push(...(page?.items ?? [])));
      }

      return items;
    }

    // Metadata is optional in the client contract. Preserve the old sequential
    // fallback when the server does not tell us how many pages exist.
    let batch = firstPage?.items ?? [];
    for (let page = 2; batch.length >= limit && page <= MAX_PAGES; page += 1) {
      const response = await fetchPage(page, limit);
      batch = response?.items ?? [];
      items.push(...batch);
    }

    return items;
  }

  private async refreshConfigurationReport(): Promise<void> {
    this.loadingReport = true;
    this.changeDetector.markForCheck();
    try {
      const plan = await firstValueFrom(this.policyService.getPlanObservable());
      if (!plan?.id) {
        this.configurationReport = { sections: [] };
        return;
      }

      if (this.cells.length === 0) {
        this.cells = await this.fetchAllCellsForPlan();
        await this.loadFeeTypeElementsForFormulas();
      }

      const elementById = new Map<string, Element>();
      const dimensionById = new Map(this.dimensions.map(dimension => [dimension.id, dimension]));
      this.configurationReport = buildConfigurationReport(this.cells, elementById, dimensionById);
      this.pruneReportDrafts();
    } catch (err: unknown) {
      this.configurationReport = { sections: [] };
      this.cancelReportChanges();
      this.toastService.show({
        type: 'error',
        title: 'Failed to load configuration report',
        text: this.getRequestErrorMessage(err, 'Unable to load the configuration report. Please try again.'),
      });
    } finally {
      this.loadingReport = false;
      // This page is projected into OnPush vs-tabs; async completion alone does not refresh
      // that ancestor view, so the report stayed blank until a later hover forced CD.
      this.changeDetector.detectChanges();
    }
  }

  private buildMatrix() {
    // Helper function to get elements for a dimension, respecting filter selections
    const getElementsForDimension = (dim: SelectedDimension): Element[] => {
      // Check if this dimension is also used as a filter
      const filterDim = this.selectedFilterDimensions.find(f => f.dimensionId === dim.dimensionId);
      if (filterDim && filterDim.selectedElementIds && filterDim.selectedElementIds.length > 0) {
        // If it's a filter, only return selected elements
        const selectedIds = filterDim.selectedElementIds;
        return dim.elements.filter(e => selectedIds.includes(e.id));
      }
      // Otherwise, return all elements
      return dim.elements;
    };

    // For Make/Model dimensions, enforce alphabetical parent-first rendering
    // so makes and their children are grouped and easy to scan.
    const getRowElementsForDimension = (dim: SelectedDimension): Element[] => {
      const elements = getElementsForDimension(dim);
      if (!this.isHierarchicalDimension(dim.dimension)) {
        return elements;
      }

      return this.orderElementsParentFirst(elements);
    };

    const getColumnElementsForDimension = (dim: SelectedDimension): Element[] => {
      const elements = getElementsForDimension(dim);
      if (!this.isHierarchicalDimension(dim.dimension)) {
        return elements;
      }

      return this.orderElementsParentFirst(elements);
    };

    // For filter dimensions, only use selected elements
    const filterElementArrays = this.selectedFilterDimensions.map(d => {
      if (d.selectedElementIds && d.selectedElementIds.length > 0) {
        const selectedIds = d.selectedElementIds;
        return d.elements.filter(e => selectedIds.includes(e.id));
      }
      return d.elements;
    });
    const filterCombinations = filterElementArrays.length > 0 ? this.buildCombinations(filterElementArrays) : [[]];

    // For row/col dimensions, if they're also filters, only use selected filter elements
    const rowCombinations =
      this.selectedRowDimensions.length > 0
        ? this.buildCombinations(this.selectedRowDimensions.map(d => getRowElementsForDimension(d)))
        : [];

    const colCombinations =
      this.selectedColDimensions.length > 0
        ? this.buildCombinations(this.selectedColDimensions.map(d => getColumnElementsForDimension(d)))
        : [];

    this.matrixRows = rowCombinations.map(combo => ({
      rowKey: combo.map(e => e.id).join('|'),
      rowLabels: combo.map((element, index) => {
        const dimension = this.selectedRowDimensions[index]?.dimension;
        if (!dimension || !this.isHierarchicalDimension(dimension)) return element.title;

        return this.buildElementHierarchyLabel(element);
      }),
      rowElementIds: combo.map(e => e.id),
    }));

    this.matrixColumns = colCombinations.map(combo => ({
      colKey: combo.map(e => e.id).join('|'),
      colLabels: combo.map((element, index) => {
        const dimension = this.selectedColDimensions[index]?.dimension;
        if (!dimension || !this.isHierarchicalDimension(dimension)) return element.title;

        return this.buildElementHierarchyLabel(element);
      }),
      colElementIds: combo.map(e => e.id),
    }));

    const cellMap = new Map<string, Cell>();
    this.cells.forEach(cell => {
      const key = this.keyFor(cell.elements.map(e => e.elementId));
      cellMap.set(key, cell);
    });

    this.matrixCells.clear();
    this.displayedMatrixCells.clear();

    // Support 1D matrices: when only rows or only columns are configured, use a
    // placeholder on the empty axis so cells can still be created and edited.
    const effectiveRows: MatrixRow[] =
      this.matrixRows.length > 0 ? this.matrixRows : [{ rowKey: EMPTY_AXIS_KEY, rowLabels: [], rowElementIds: [] }];
    const effectiveColumns: MatrixColumn[] =
      this.matrixColumns.length > 0
        ? this.matrixColumns
        : [{ colKey: EMPTY_AXIS_KEY, colLabels: [], colElementIds: [] }];
    const displayedMatrixCellKeys = new Map<string, Map<string, string>>();

    filterCombinations.forEach((filterCombo, filterCombinationIndex) => {
      effectiveRows.forEach(row => {
        effectiveColumns.forEach(col => {
          if (row.rowElementIds.length === 0 && col.colElementIds.length === 0) {
            return;
          }

          const filterElementIds = this.getApplicableFilterElementIds(
            filterCombo,
            row.rowElementIds,
            col.colElementIds
          );
          const filterKey = filterElementIds.length > 0 ? filterElementIds.join('|') : '';
          // Deduplicate element IDs in case a dimension is used in multiple roles.
          // allElementIds drives the cell lookup because cells are stored with all their
          // element IDs (row + col + filter-only dimensions), so the lookup key must match.
          const allElementIds = Array.from(new Set([...row.rowElementIds, ...col.colElementIds, ...filterElementIds]));
          const cellLookupKey = this.keyFor(allElementIds);
          const cellKey = filterKey ? `${row.rowKey}|${col.colKey}|${filterKey}` : `${row.rowKey}|${col.colKey}|`;
          const existing = cellMap.get(cellLookupKey);

          const view: CellView = {
            rowKey: row.rowKey,
            colKey: col.colKey,
            filterElementIds,
            allElementIds,
            key: cellLookupKey,
            cell: existing,
            value: existing?.value != null ? Number(existing.value) : null,
            valueType: existing?.valueType ?? 'FIXED',
            formula: existing?.formula ?? null,
            canUseFormula: this.hasSellerAndFeeType(allElementIds.map(id => this.elementDimensionMap[id])),
            effectiveFrom: this.normalizeDateInput(existing?.effectiveFrom ?? null),
            effectiveTo: this.normalizeDateInput(existing?.effectiveTo ?? null),
            dirty: false,
          };

          this.matrixCells.set(cellKey, view);

          // Remember the key for the first active filter combination. Resolve
          // the view after the full map is built because overlapping row/filter
          // dimensions can intentionally collapse several combinations to the
          // same final matrixCells entry.
          if (filterCombinationIndex === 0) {
            let rowCellKeys = displayedMatrixCellKeys.get(row.rowKey);
            if (!rowCellKeys) {
              rowCellKeys = new Map<string, string>();
              displayedMatrixCellKeys.set(row.rowKey, rowCellKeys);
            }
            rowCellKeys.set(col.colKey, cellKey);
          }
        });
      });
    });

    displayedMatrixCellKeys.forEach((rowCellKeys, rowKey) => {
      const rowCells = new Map<string, CellView>();
      rowCellKeys.forEach((cellKey, colKey) => {
        const view = this.matrixCells.get(cellKey);
        if (view) {
          rowCells.set(colKey, view);
        }
      });
      this.displayedMatrixCells.set(rowKey, rowCells);
    });
  }

  private isMakeModelDimension(dimension: Dimension): boolean {
    const normalized = dimension.title.toLowerCase().replace(/[\s\-_/]+/g, '');
    return (
      normalized === 'makemodel' ||
      normalized === 'ymm' ||
      (normalized.includes('make') && normalized.includes('model'))
    );
  }

  /**
   * Dimensions whose elements are nested (Make/Model, Seller with sub-distributors, ...) are
   * rendered parent-first with hierarchy labels.
   */
  private isHierarchicalDimension(dimension: Dimension): boolean {
    if (this.isMakeModelDimension(dimension)) return true;

    const elements = this.allElements.get(dimension.id) ?? [];
    return elements.some(element => !!element.parentId);
  }

  private orderElementsParentFirst(elements: Element[]): Element[] {
    if (elements.length <= 1) {
      return elements;
    }

    const elementById = new Map(elements.map(el => [el.id, el]));
    const childrenByParent = new Map<string, Element[]>();
    const roots: Element[] = [];

    elements.forEach(element => {
      const parentId = element.parentId;
      if (parentId && elementById.has(parentId)) {
        const siblings = childrenByParent.get(parentId) ?? [];
        siblings.push(element);
        childrenByParent.set(parentId, siblings);
        return;
      }

      roots.push(element);
    });

    const compareElementsByTitle = (left: Element, right: Element): number => {
      const titleComparison = left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
      if (titleComparison !== 0) {
        return titleComparison;
      }

      return left.id.localeCompare(right.id);
    };

    roots.sort(compareElementsByTitle);
    for (const siblings of childrenByParent.values()) {
      siblings.sort(compareElementsByTitle);
    }

    const ordered: Element[] = [];
    const visited = new Set<string>();
    const appendTree = (element: Element) => {
      if (visited.has(element.id)) {
        return;
      }

      visited.add(element.id);
      ordered.push(element);

      const children = childrenByParent.get(element.id) ?? [];
      children.forEach(child => appendTree(child));
    };

    roots.forEach(root => appendTree(root));

    elements.forEach(element => {
      if (!visited.has(element.id)) {
        ordered.push(element);
      }
    });

    return ordered;
  }

  private buildElementHierarchyLabel(element: Element): string {
    const dimensionElements = this.allElements.get(element.dimensionId) ?? [];
    const byId = new Map<string, Element>(dimensionElements.map(candidate => [candidate.id, candidate]));

    const segments: string[] = [];
    const visited = new Set<string>();
    let cursor: Element | undefined = byId.get(element.id) ?? element;

    while (cursor && !visited.has(cursor.id)) {
      visited.add(cursor.id);
      segments.unshift(cursor.title);

      if (!cursor.parentId) break;
      cursor = byId.get(cursor.parentId) ?? cursor.parent ?? undefined;
    }

    return segments.join(' - ');
  }

  private buildCombinations(elementArrays: Element[][]): Element[][] {
    if (elementArrays.length === 0) return [[]];
    if (elementArrays.length === 1) return elementArrays[0].map(e => [e]);

    const [first, ...rest] = elementArrays;
    const restCombos = this.buildCombinations(rest);
    const result: Element[][] = [];
    first.forEach(element => {
      restCombos.forEach(combo => {
        result.push([element, ...combo]);
      });
    });
    return result;
  }

  getCellView(rowKey: string, colKey: string): CellView | undefined {
    return this.displayedMatrixCells.get(rowKey)?.get(colKey);
  }

  trackMatrixRow(_index: number, row: MatrixRow): string {
    return row.rowKey;
  }

  trackMatrixColumn(_index: number, column: MatrixColumn): string {
    return column.colKey;
  }

  private getApplicableFilterElementIds(
    filterElements: Element[],
    rowElementIds: string[],
    colElementIds: string[]
  ): string[] {
    const representedDimensionIds = new Set(
      [...rowElementIds, ...colElementIds]
        .map(elementId => this.elementDimensionMap[elementId])
        .filter((dimensionId): dimensionId is string => !!dimensionId)
    );

    return filterElements
      .filter(element => !representedDimensionIds.has(element.dimensionId))
      .map(element => element.id)
      .sort();
  }

  onValueChange(view: CellView, value: string | number | null) {
    const strValue = value === null || value === undefined ? '' : String(value);
    const numVal = strValue === '' ? null : Number(strValue);
    view.value = Number.isFinite(numVal as number) ? (numVal as number) : null;
    view.dirty = true;
  }

  onEffectiveDateChange(view: CellView, field: 'effectiveFrom' | 'effectiveTo', value: string | null) {
    view[field] = this.normalizeDateInput(value);
    view.dirty = true;
  }

  openEffectiveDateModal(view: CellView, dialog: { open: () => void }) {
    const nextMatrixCellKey =
      Array.from(this.matrixCells.entries()).find(([, candidate]) => candidate === view)?.[0] ?? null;
    const nextEffectiveFrom = this.normalizeDateInput(view.effectiveFrom);
    const nextEffectiveTo = this.normalizeDateInput(view.effectiveTo);
    const renderToken = ++this.effectiveDateModalRenderToken;

    // Clear stale picker state before binding the newly clicked cell.
    this.renderEffectiveDatePickers = false;
    this.effectiveDateModalView = null;
    this.effectiveDateModalMatrixCellKey = null;
    this.effectiveDateModalFrom = null;
    this.effectiveDateModalTo = null;

    this.effectiveDateModalView = view;
    this.effectiveDateModalMatrixCellKey = nextMatrixCellKey;
    this.effectiveDateModalFrom = nextEffectiveFrom;
    this.effectiveDateModalTo = nextEffectiveTo;
    this.effectiveDateModalRecipient = view.cell?.recipientRole
      ? { role: view.cell.recipientRole, partyRecordId: view.cell.recipientPartyId ?? null }
      : null;
    this.effectiveDateModalInitialRecipientKey = this.recipientKey(this.effectiveDateModalRecipient);

    dialog.open();

    setTimeout(() => {
      if (this.effectiveDateModalRenderToken !== renderToken) return;
      this.renderEffectiveDatePickers = true;
    }, 0);
  }

  closeEffectiveDateModal(dialog?: { close: () => void }) {
    this.effectiveDateModalRenderToken++;
    dialog?.close();
    this.renderEffectiveDatePickers = false;
    this.effectiveDateModalView = null;
    this.effectiveDateModalMatrixCellKey = null;
    this.effectiveDateModalFrom = null;
    this.effectiveDateModalTo = null;
    this.effectiveDateModalRecipient = null;
  }

  async saveEffectiveDateModal(dialog?: { close: () => void }) {
    const view = this.effectiveDateModalView;
    if (!view) return;

    const nextEffectiveFrom = this.normalizeDateInput(this.effectiveDateModalFrom);
    const nextEffectiveTo = this.normalizeDateInput(this.effectiveDateModalTo);
    const nextRecipient = this.effectiveDateModalRecipient;
    const recipientChanged = this.recipientKey(nextRecipient) !== this.effectiveDateModalInitialRecipientKey;

    if (this.effectiveDateModalMatrixCellKey && this.matrixCells.has(this.effectiveDateModalMatrixCellKey)) {
      const current = this.matrixCells.get(this.effectiveDateModalMatrixCellKey);
      if (current) {
        this.matrixCells.set(this.effectiveDateModalMatrixCellKey, {
          ...current,
          effectiveFrom: nextEffectiveFrom,
          effectiveTo: nextEffectiveTo,
          dirty: true,
        });
      }
    }

    view.effectiveFrom = nextEffectiveFrom;
    view.effectiveTo = nextEffectiveTo;
    view.dirty = true;

    this.closeEffectiveDateModal(dialog);

    await this.saveChanges();

    // Recipient is allocation metadata on an existing cell — its own endpoint, never part of the value save.
    if (recipientChanged && view.cell?.id) {
      try {
        await this.ratingMatrixService.setCellRecipient(view.cell.id, nextRecipient);
        view.cell.recipientRole = nextRecipient?.role ?? null;
        view.cell.recipientPartyId = nextRecipient?.partyRecordId ?? null;
        void this.refreshRecipientReview();
      } catch (err) {
        this.toastService.show({
          type: 'error',
          title: 'Recipient not saved',
          text: escapeHtml(
            (err as { error?: { message?: string } })?.error?.message ?? 'Unable to save the cell recipient.'
          ),
        });
      }
    }

    if (this.hasAssetValues() && this.selectedTermId) {
      await this.calculatePricingPreview();
    }
  }

  recipientKey(recipient: ContractLedger.RecipientAssignment | null | undefined): string {
    return recipient ? `${recipient.role}|${recipient.partyRecordId ?? ''}` : '';
  }

  /** Preview label for a matched cell's effective recipient. */
  describeRecipient(recipient: PricingPreview['intersections'][number]['recipient']): string {
    if (!recipient) return 'No recipient assigned';
    const name = recipient.party.partyName ?? (recipient.party.partyId ? recipient.party.partyId : 'unidentified');
    const source =
      recipient.source === ContractLedger.RecipientSource.CELL
        ? 'cell override'
        : recipient.source === ContractLedger.RecipientSource.SELLER_DIMENSION
          ? "cell's Seller"
          : 'fee type default';
    return `${name} (${ContractLedger.partyTypeLabel(recipient.party.type).toLowerCase()}, ${source})`;
  }

  isEffectiveDateModalValid(): boolean {
    if (!this.effectiveDateModalFrom || !this.effectiveDateModalTo) return true;
    return this.effectiveDateModalFrom <= this.effectiveDateModalTo;
  }

  private formatDateForDisplay(value: string | null | undefined): string {
    if (!value) return 'Open';
    const normalized = this.normalizeDateInput(value);
    if (!normalized) return 'Open';

    const [year, month, day] = normalized.split('-');
    if (!year || !month || !day) return normalized;
    return `${day}-${month}-${year}`;
  }

  getEffectiveWindowLabel(view: CellView): string {
    if (!view.effectiveFrom && !view.effectiveTo) return 'Always';
    if (view.effectiveFrom && !view.effectiveTo) return `Starts on ${view.effectiveFrom}`;
    if (!view.effectiveFrom && view.effectiveTo) return `Ends on ${view.effectiveTo}`;
    return `${view.effectiveFrom} to ${view.effectiveTo}`;
  }

  isEffectiveWindowValid(view: CellView): boolean {
    if (!view.effectiveFrom || !view.effectiveTo) return true;
    return view.effectiveFrom <= view.effectiveTo;
  }

  clearAsOfDate() {
    this.asOfDate = null;
    if (this.hasAssetValues() && this.selectedTermId) {
      this.calculatePricingPreview();
    }
  }

  // Re-price the preview whenever the selected distributor changes.
  // Coverage requirements can key on the distributor, so rebuild the add-on list before re-pricing.
  async onDistributorSelected(distributor: Distributor.Model | null) {
    this.selectedDistributor = distributor;
    this.selectedDistributorId = distributor?.id ?? null;

    await this.loadAvailableCoverages();

    if (this.hasAssetValues() && this.selectedTermId) {
      this.calculatePricingPreview();
    }
  }

  onAsOfDateSelectionChanged(value: Date | null) {
    this.asOfDate = this.normalizeDateInput(value);
  }

  openAsOfDateModal(dialog: { open: () => void }) {
    this.renderAsOfDatePicker = false;
    this.asOfDateModalValue = this.asOfDate ?? this.getLocalTodayIsoDate();
    dialog.open();

    setTimeout(() => {
      this.renderAsOfDatePicker = true;
    }, 0);
  }

  closeAsOfDateModal(dialog?: { close: () => void }) {
    dialog?.close();
    this.renderAsOfDatePicker = false;
    this.asOfDateModalValue = null;
  }

  onAsOfDateModalSelectionChanged(value: Date | null) {
    this.asOfDateModalValue = this.normalizeDateInput(value);
  }

  async saveAsOfDateModal(dialog?: { close: () => void }) {
    this.asOfDate = this.asOfDateModalValue;
    this.closeAsOfDateModal(dialog);

    if (this.hasAssetValues() && this.selectedTermId) {
      await this.calculatePricingPreview();
    }
  }

  getDisplayedAsOfDate(): string {
    return this.asOfDate ?? this.getLocalTodayIsoDate();
  }

  getAsOfDateButtonLabel(): string {
    return this.formatDateForDisplay(this.getDisplayedAsOfDate());
  }

  onEffectiveDateModalFromSelectionChanged(value: Date | null) {
    this.effectiveDateModalFrom = this.normalizeDateInput(value);
  }

  onEffectiveDateModalToSelectionChanged(value: Date | null) {
    this.effectiveDateModalTo = this.normalizeDateInput(value);
  }

  toDateValue(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = this.normalizeDateInput(value);
    if (!normalized) return null;

    const [yearText, monthText, dayText] = normalized.split('-');
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;

    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private getLocalTodayIsoDate(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = `${now.getMonth() + 1}`.padStart(2, '0');
    const day = `${now.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Recomputes the cached flag that indicates whether any dimension that is
   * currently participating in the matrix (row, column, or filter) is marked
   * as a pricing dimension.
   */
  private updateHasPricingDimensionInMatrix(): void {
    const selectedDims: SelectedDimension[] = [
      ...this.selectedRowDimensions,
      ...this.selectedColDimensions,
      ...this.selectedFilterDimensions,
    ];
    this.hasPricingDimensionInMatrix = selectedDims.some(d => d.dimension?.isPricing);
  }

  canUseBooleanValues(): boolean {
    // Boolean values are allowed when no pricing dimensions are included
    // in the current matrix (rows, columns, or filters). If a pricing
    // dimension participates in the matrix, cells must remain numeric.
    return !this.hasPricingDimensionInMatrix;
  }

  onBooleanCellToggle(view: CellView, checked: boolean) {
    // Represent booleans using numeric 1/0 so that the backend schema and
    // dynamic pricing engine (when used elsewhere) continue to work with
    // numeric values.
    view.value = checked ? 1 : 0;
    view.dirty = true;
  }

  async saveChanges() {
    // Clears first, so moving a make-model between classes frees the old class before the new one is set
    const isClearedEdit = (view: CellView) => view.valueType !== 'FORMULA' && (view.value === null || view.value === 0);
    const edits = Array.from(this.matrixCells.values())
      .filter(v => v.dirty)
      .sort((a, b) => Number(!isClearedEdit(a)) - Number(!isClearedEdit(b)));
    if (!edits.length) return;

    const invalidDateRange = edits.find(edit => !this.isEffectiveWindowValid(edit));
    if (invalidDateRange) {
      this.toastService.show({
        type: 'error',
        title: 'Invalid effective date range',
        text: 'Effective From must be on or before Effective To.',
      });
      return;
    }

    this.saving = true;

    try {
      for (const view of edits) {
        const plan = await firstValueFrom(this.policyService.getPlanObservable());
        if (!plan?.id) {
          throw new Error('A plan must be selected to create rating cells.');
        }
        const planId = plan.id;
        // A formula cell's value is always 0, so it must never read as "cleared" here.
        const isFormulaCell = view.valueType === 'FORMULA';
        const shouldDeleteCell = isClearedEdit(view);
        const valueFields = isFormulaCell
          ? { valueType: 'FORMULA' as const, formula: view.formula }
          : { value: view.value ?? 0, ...(view.cell?.valueType === 'FORMULA' ? { valueType: 'FIXED' as const } : {}) };

        if (view.cell?.id) {
          if (shouldDeleteCell) {
            await this.ratingMatrixService.deleteCell(view.cell.id);
            view.cell = undefined;
          } else {
            await this.ratingMatrixService.updateCell(view.cell.id, {
              planId,
              ...valueFields,
              effectiveFrom: view.effectiveFrom ?? null,
              effectiveTo: view.effectiveTo ?? null,
            });
          }
        } else {
          if (shouldDeleteCell) {
            // No existing cell + empty value => nothing to persist.
            continue;
          }

          try {
            await this.ratingMatrixService.createCell({
              planId,
              value: isFormulaCell ? 0 : (view.value ?? 0),
              ...(isFormulaCell ? { valueType: 'FORMULA' as const, formula: view.formula } : {}),
              elementIds: view.allElementIds,
              effectiveFrom: view.effectiveFrom ?? null,
              effectiveTo: view.effectiveTo ?? null,
            });
          } catch (createError: unknown) {
            // If cell already exists, try to find and update it instead
            const error = createError as { error?: { message?: string } };
            if (error?.error?.message?.includes('already exists')) {
              // Reload cells to get the existing cell
              await this.loadCells();
              this.buildMatrix();
              // Try to find the cell again
              const updatedView = this.getCellView(view.rowKey, view.colKey);
              if (updatedView?.cell?.id) {
                await this.ratingMatrixService.updateCell(updatedView.cell.id, {
                  planId,
                  ...valueFields,
                  effectiveFrom: view.effectiveFrom ?? null,
                  effectiveTo: view.effectiveTo ?? null,
                });
              } else {
                throw createError;
              }
            } else {
              throw createError;
            }
          }
        }

        // Landed, so a retry after a later failure doesn't resend it
        view.dirty = false;
      }

      await this.loadCells();
      this.buildMatrix();
      this.toastService.show({
        type: 'success',
        title: 'Changes saved',
        text: 'Rating matrix values have been saved successfully.',
      });
    } catch (err: unknown) {
      this.toastService.show({
        type: 'error',
        title: 'Failed to save changes',
        text: this.getRequestErrorMessage(err, 'Unable to save changes. Please try again.'),
      });
    } finally {
      this.saving = false;
    }
  }

  private keyFor(ids: string[]) {
    return [...ids].sort().join('|');
  }

  private normalizeDateInput(value: unknown): string | null {
    if (value === null || value === undefined) return null;

    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) return null;
      return this.toLocalDateOnlyString(value);
    }

    if (typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;
      return this.toLocalDateOnlyString(parsed);
    }

    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) return null;

      const dateOnlyMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
      if (dateOnlyMatch?.[1]) {
        return dateOnlyMatch[1];
      }

      const parsed = new Date(normalized);
      if (Number.isNaN(parsed.getTime())) return null;
      return this.toLocalDateOnlyString(parsed);
    }

    return null;
  }

  private toLocalDateOnlyString(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  isDimensionSelected(dimensionId: string): 'row' | 'col' | 'filter' | null {
    if (this.selectedRowDimensions.some(d => d.dimensionId === dimensionId)) return 'row';
    if (this.selectedColDimensions.some(d => d.dimensionId === dimensionId)) return 'col';
    if (this.selectedFilterDimensions.some(d => d.dimensionId === dimensionId)) return 'filter';
    return null;
  }

  async loadAssets() {
    this.loadingAssets = true;
    try {
      const res = await this.assetService.listAssets({ page: 1, limit: 100 });
      if (res) {
        this.assets = res.items;
      } else {
        this.assets = [];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      this.toastService.show({
        type: 'error',
        title: 'Failed to load assets',
        text: err?.message ?? 'Unable to load assets. Please try again.',
      });
      this.assets = [];
    } finally {
      this.loadingAssets = false;
    }
  }

  async openAssetPickerModal() {
    // Get the property set ID from the current policy
    const policy = await firstValueFrom(this.policyService.getPolicyObservable());
    this.propertySetId = policy?.propertySetId ?? null;
    const hasPersistedValues =
      Object.keys(this.assetValues || {}).length > 0 || Object.keys(this.assetDimensionValues || {}).length > 0;
    this.assetInfo.setValue(
      hasPersistedValues
        ? {
            propertyValues: this.assetValues,
            dimensionValues: this.assetDimensionValues,
          }
        : null,
      { emitEvent: false }
    );
    this.showAssetPickerModal = true;
  }

  closeAssetPickerModal() {
    this.showAssetPickerModal = false;
  }

  confirmAssetInput() {
    const values = this.assetInfo.value;
    if (!values || !values.propertyValues || Object.keys(values.propertyValues).length === 0) {
      this.toastService.show({
        type: 'error',
        title: 'Invalid input',
        text: 'Please enter asset details to calculate pricing.',
      });
      return;
    }

    this.showAssetPickerModal = false;
    this.assetValues = values.propertyValues;
    this.assetDimensionValues = values.dimensionValues ?? {};
    void this.preloadAssetDimensionElements(this.assetDimensionValues);
    // Ensure term is selected before calculating
    if (!this.selectedTermId && this.termElements.length > 0) {
      this.selectedTermId = this.termElements[0].id;
    }
    void this.ratingMatrixService.syncVehicleMakeModelElements(values.propertyValues);
    this.calculatePricingPreview();
  }

  onAssetSelected(asset: unknown) {
    if (!asset || typeof asset !== 'object' || !('id' in asset)) {
      this.selectedAsset = null;
      this.showAssetPickerModal = false;
      this.assetValues = {};
      this.assetDimensionValues = {};
      this.pricingPreview = null;
      return;
    }

    const assetModel = asset as Asset.Model;
    this.selectedAsset = assetModel;
    this.showAssetPickerModal = false;
    this.loadAssetValuesAndCalculate(assetModel.id);
  }

  async loadAssetValuesAndCalculate(assetId: string) {
    // Ensure terms and addons are loaded before calculating
    if (this.termElements.length === 0) {
      await this.loadTermsAndAddons();
    }
    // Ensure a term is selected if available
    if (!this.selectedTermId && this.termElements.length > 0) {
      this.selectedTermId = this.termElements[0].id;
    }
    try {
      const submission = await this.assetService.getAssetValues(assetId);
      if (submission?.value) {
        this.assetValues = submission.value;
        this.assetDimensionValues = submission.dimensionValues ?? {};
        void this.preloadAssetDimensionElements(this.assetDimensionValues);
        this.assetInfo.setValue(
          {
            propertyValues: this.assetValues,
            dimensionValues: this.assetDimensionValues,
          },
          { emitEvent: false }
        );
        // Trigger calculation after asset values are loaded
        await this.calculatePricingPreview();
      } else {
        this.assetValues = {};
        this.assetDimensionValues = {};
        this.pricingPreview = null;
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      this.toastService.show({
        type: 'error',
        title: 'Failed to load asset values',
        text: error?.message ?? 'Unable to load asset values. Please try again.',
      });
      this.assetValues = {};
      this.assetDimensionValues = {};
      this.pricingPreview = null;
    }
  }

  async loadTermsAndAddons() {
    try {
      const plan = await firstValueFrom(this.policyService.getPlanObservable());

      // Load term dimension elements (synced from policy terms)
      const termDimension = this.dimensions.find(d => d.isSystem && d.title.toLowerCase().includes('term'));
      if (termDimension) {
        try {
          // Term dimension requires policyId for system dimensions
          const shouldScopeToPolicy =
            termDimension.isSystem && (termDimension.title === 'Coverage' || termDimension.title === 'Term');
          if (shouldScopeToPolicy && !this.currentPolicyId) {
            console.warn('Policy not loaded, cannot load term dimension elements');
            this.termElements = [];
          } else {
            const res = await this.ratingMatrixService.listElements(termDimension.id, {
              ...(shouldScopeToPolicy && this.currentPolicyId ? { policyId: this.currentPolicyId } : {}),
            });
            if (res) {
              this.termElements = res.items;
              // Set default term if none selected and term elements are available
              if (!this.selectedTermId && this.termElements.length > 0) {
                this.selectedTermId = this.termElements[0].id;
              }
            }
          }
        } catch (err) {
          console.error('Failed to load term dimension elements:', err);
          this.termElements = [];
        }
      } else {
        this.termElements = [];
      }

      // Load pricing dimensions
      this.pricingDimensions = this.dimensions.filter(d => d.isPricing);
      // Auto-select default pricing dimensions:
      // - Prefer system pricing dimensions
      // - Fallback to the first pricing dimension if no system pricing dimensions exist
      const defaultPricingDimensionIds = this.getDefaultPricingDimensionIds();
      this.selectedPricingDimensionIds = defaultPricingDimensionIds;

      if (plan?.addons) {
        this.planAddons = plan.addons;
      }

      // Load available coverages if a term is already selected
      if (this.selectedTermId) {
        await this.loadAvailableCoverages();
      }
    } catch (err) {
      console.error('Failed to load terms and addons:', err);
    }
  }

  async onTermChange(termId: string | readonly string[] | null) {
    // Handle null or array cases - return early if not a valid string
    if (!termId) {
      return;
    }
    // Check if it's an array (including readonly arrays)
    if (typeof termId !== 'string') {
      return;
    }
    // At this point, termId is definitely a string
    console.log('Term changed', { termId, hasAssetValues: this.hasAssetValues() });
    this.selectedTermId = termId;
    // Reset addons and optional coverages when term changes
    this.selectedAddonIds = [];
    this.selectedOptionalCoverageIds = [];
    // Load available coverages for the selected term
    await this.loadAvailableCoverages();
    // Recalculate pricing if asset values exist
    if (this.hasAssetValues()) {
      console.log('Triggering calculation after term change');
      this.calculatePricingPreview();
    } else {
      console.log('Term changed but no asset values or coverage selected yet');
    }
  }

  async loadAvailableCoverages() {
    if (!this.selectedTermId) {
      this.availableCoverages = [];
      this.alwaysIncludedCoverages = [];
      this.selectedOptionalCoverageIds = [];
      this.selectedOptionalCoverageOptionIds = [];
      return;
    }

    try {
      const plan = await firstValueFrom(this.policyService.getPlanObservable());
      const policy = await firstValueFrom(this.policyService.getPolicyObservable());

      if (!plan || !policy) {
        this.availableCoverages = [];
        this.alwaysIncludedCoverages = [];
        this.selectedOptionalCoverageIds = [];
        this.selectedOptionalCoverageOptionIds = [];
        return;
      }

      const term = this.resolveSelectedPolicyTerm(policy);
      if (!term) {
        this.availableCoverages = [];
        this.alwaysIncludedCoverages = [];
        this.selectedOptionalCoverageIds = [];
        this.selectedOptionalCoverageOptionIds = [];
        return;
      }

      let termCoverageConfigs = plan.coverageConfigs?.[term.id];

      // Plan objects in this view can be term-sliced by other screens/services.
      // If the selected term key is missing, fetch that term's configs on demand.
      if (termCoverageConfigs === undefined && plan.id && plan.policyId) {
        const refreshedTermPlan = await this.policyService.getPlan(plan.policyId, plan.id, {
          includeCoverageConfigs: true,
          includeFees: false,
          termId: term.id,
          includeCoverageConfigBlocks: true,
        });

        termCoverageConfigs = refreshedTermPlan?.coverageConfigs?.[term.id] ?? null;

        if (plan.coverageConfigs) {
          plan.coverageConfigs[term.id] = termCoverageConfigs;
        }
      }

      const effectiveTermCoverageConfigs = termCoverageConfigs ?? [];
      const baseCoverages: Array<{ id: string; title: string }> = [];

      // Build optional coverage options:
      // - Individual add-ons: per-coverage options
      // - Grouped add-ons: coverages grouped by plan addon (e.g. "Medium")
      const optionalOptionsById = new Map<string, OptionalCoverageOption>();

      if (policy.coverages && policy.coverages.length > 0) {
        // Mirror backend behavior: evaluate coverage requirements against asset and distributor values.
        // Requirements can key on name/tags/parentId/permissionKey, so pass the whole selected model.
        const requirementPropertyValues = normalizePropertyValues({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ...(this.assetValues as Record<string, any>),
          ...(this.selectedDistributor ? { distributor: this.selectedDistributor } : {}),
        });

        for (const config of effectiveTermCoverageConfigs) {
          const requirement = Conditions.evaluateRule(
            config.requirement,
            requirementPropertyValues
          ) as Policy.Plan.CoverageRequirement;

          const coverage = policy.coverages.find(c => c.id === config.coverageId);
          if (!coverage) continue;

          if (requirement === Policy.Plan.CoverageRequirementValue.NOT_APPLICABLE) {
            // Never show NOT_APPLICABLE coverages
            continue;
          }

          if (requirement === Policy.Plan.CoverageRequirementValue.BASE) {
            // BASE coverages are always included in calculation and not selectable
            baseCoverages.push({ id: coverage.id, title: coverage.title });
            continue;
          }

          // Any other requirement value is treated as an add-on reference.
          // If it matches a plan addon, group by that addon (except "individual" style addons),
          // otherwise treat as an individual add-on.
          const addon = plan.addons?.find(a => a.id === requirement);

          if (addon && !addon.id.startsWith('cov_')) {
            // Grouped add-on: group by addon id/title
            const groupId = addon.id;
            let option = optionalOptionsById.get(groupId);
            if (!option) {
              option = {
                id: groupId,
                title: addon.title,
                coverageIds: [],
                isGroup: true,
              };
              optionalOptionsById.set(groupId, option);
            }
            if (!option.coverageIds.includes(coverage.id)) {
              option.coverageIds.push(coverage.id);
            }
          } else {
            // Individual add-on (no addon definition or "individual" addon id)
            const optionId = coverage.id;
            let option = optionalOptionsById.get(optionId);
            if (!option) {
              option = {
                id: optionId,
                title: coverage.title,
                coverageIds: [coverage.id],
                isGroup: false,
              };
              optionalOptionsById.set(optionId, option);
            }
          }
        }
      }

      this.alwaysIncludedCoverages = baseCoverages;
      this.availableCoverages = Array.from(optionalOptionsById.values());

      // Ensure selected optional coverage options remain valid and
      // recompute underlying coverageIds from selected option ids
      const validOptionIds = new Set(this.availableCoverages.map(o => o.id));
      this.selectedOptionalCoverageOptionIds = this.selectedOptionalCoverageOptionIds.filter(id =>
        validOptionIds.has(id)
      );

      const selectedCoverageIdSet = new Set<string>();
      for (const optionId of this.selectedOptionalCoverageOptionIds) {
        const option = this.availableCoverages.find(o => o.id === optionId);
        if (!option) continue;
        option.coverageIds.forEach(id => selectedCoverageIdSet.add(id));
      }
      this.selectedOptionalCoverageIds = Array.from(selectedCoverageIdSet);
    } catch (err) {
      console.error('Failed to load available coverages:', err);
      this.availableCoverages = [];
      this.alwaysIncludedCoverages = [];
      this.selectedOptionalCoverageIds = [];
    }
  }

  onCoveragesChange(coverageIds: string | readonly string[] | null) {
    // Normalize vs-select multi value into option ids[]
    if (!coverageIds) {
      this.selectedOptionalCoverageOptionIds = [];
    } else if (typeof coverageIds === 'string') {
      this.selectedOptionalCoverageOptionIds = [coverageIds];
    } else {
      this.selectedOptionalCoverageOptionIds = [...coverageIds];
    }

    // Derive underlying coverage ids from selected options
    const selectedCoverageIdSet = new Set<string>();
    for (const optionId of this.selectedOptionalCoverageOptionIds) {
      const option = this.availableCoverages.find(o => o.id === optionId);
      if (!option) continue;
      option.coverageIds.forEach(id => selectedCoverageIdSet.add(id));
    }
    this.selectedOptionalCoverageIds = Array.from(selectedCoverageIdSet);

    console.log('Optional coverages changed', {
      selectedOptionalCoverageOptionIds: this.selectedOptionalCoverageOptionIds,
      selectedOptionalCoverageIds: this.selectedOptionalCoverageIds,
      hasAssetValues: this.hasAssetValues(),
    });

    // Recalculate pricing if asset values exist
    if (this.hasAssetValues()) {
      this.calculatePricingPreview();
    } else {
      console.log('Optional coverages changed but no asset values yet');
    }
  }

  onAddonToggle(addonId: string) {
    const index = this.selectedAddonIds.indexOf(addonId);
    if (index > -1) {
      this.selectedAddonIds.splice(index, 1);
    } else {
      this.selectedAddonIds.push(addonId);
    }
    // Recalculate pricing if asset values exist
    if (this.hasAssetValues()) {
      this.calculatePricingPreview();
    }
  }

  onAddonsChange(addonIds: string | readonly string[] | null) {
    // Normalize vs-select multi value into a string[]
    if (!addonIds) {
      this.selectedAddonIds = [];
    } else if (typeof addonIds === 'string') {
      this.selectedAddonIds = [addonIds];
    } else {
      this.selectedAddonIds = [...addonIds];
    }

    console.log('Add-ons changed', {
      selectedAddonIds: this.selectedAddonIds,
      hasAssetValues: this.hasAssetValues(),
    });

    // Recalculate pricing if asset values exist
    if (this.hasAssetValues()) {
      this.calculatePricingPreview();
    }
  }

  isAddonSelected(addonId: string): boolean {
    return this.selectedAddonIds.includes(addonId);
  }

  onPricingDimensionsChange(dimensionIds: string | readonly string[] | null) {
    // Normalize vs-select multi value into a string[]
    let nextSelection: string[];

    if (!dimensionIds) {
      nextSelection = [];
    } else if (typeof dimensionIds === 'string') {
      nextSelection = [dimensionIds];
    } else {
      nextSelection = [...dimensionIds];
    }

    // Enforce at least one pricing dimension selected.
    // If the user clears all selections, fall back to default pricing dimensions.
    if (nextSelection.length === 0) {
      nextSelection = this.getDefaultPricingDimensionIds();
    }

    this.selectedPricingDimensionIds = nextSelection;

    // Recalculate pricing if asset values exist
    if (this.hasAssetValues()) {
      this.calculatePricingPreview();
    }
  }

  isPricingDimensionSelected(dimensionId: string): boolean {
    return this.selectedPricingDimensionIds.includes(dimensionId);
  }

  /**
   * Determine default pricing dimensions:
   * - Prefer all dimensions where isPricing && isSystem
   * - If none, fall back to the first pricing dimension (if available)
   */
  private getDefaultPricingDimensionIds(): string[] {
    if (!this.pricingDimensions || this.pricingDimensions.length === 0) {
      return [];
    }

    const systemPricingDimensions = this.pricingDimensions.filter(d => d.isSystem);
    if (systemPricingDimensions.length > 0) {
      return systemPricingDimensions.map(d => d.id);
    }

    const first = this.pricingDimensions[0];
    return first ? [first.id] : [];
  }

  async calculatePricingPreview() {
    console.log('=== calculatePricingPreview called ===', {
      hasAssetValues: !!this.assetValues && Object.keys(this.assetValues).length > 0,
      assetValuesKeys: this.assetValues ? Object.keys(this.assetValues) : [],
      assetValuesCount: this.assetValues ? Object.keys(this.assetValues).length : 0,
      selectedTermId: this.selectedTermId,
      termElementsCount: this.termElements.length,
      termElements: this.termElements.map(te => ({ id: te.id, title: te.title })),
      pricingDimensionsCount: this.pricingDimensions.length,
    });

    if (!this.assetValues || Object.keys(this.assetValues).length === 0) {
      console.log('❌ No asset values, returning');
      this.pricingPreview = null;
      this.loadingPricing = false;
      return;
    }

    if (!this.selectedTermId) {
      console.log('❌ No term selected, returning');
      this.pricingPreview = null;
      this.loadingPricing = false;
      return;
    }

    this.loadingPricing = true;
    try {
      const plan = await firstValueFrom(this.policyService.getPlanObservable());
      const policy = await firstValueFrom(this.policyService.getPolicyObservable());

      if (!plan?.id || !policy?.terms || policy.terms.length === 0) {
        console.log('❌ Missing plan or policy terms', { planId: plan?.id, termsCount: policy?.terms?.length });
        this.pricingPreview = null;
        this.loadingPricing = false;
        return;
      }

      // Find the term element that was selected
      const selectedTermElement = this.getSelectedTermElement();
      if (!selectedTermElement) {
        console.log('Selected term element not found', {
          selectedTermId: this.selectedTermId,
          termElements: this.termElements,
        });
        this.pricingPreview = null;
        return;
      }

      const term = this.resolveSelectedPolicyTerm(policy);
      if (!term) {
        console.log('❌ Policy term not found for term element', {
          termElementId: selectedTermElement.id,
          termElementTitle: selectedTermElement.title,
          termElementSortOrder: selectedTermElement.sortOrder,
          termElementTitleLower: selectedTermElement.title.toLowerCase().trim(),
          policyTerms: policy.terms.map(t => ({
            id: t.id,
            title: t.title,
            order: t.order,
            titleLower: t.title.toLowerCase().trim(),
          })),
        });
        this.pricingPreview = null;
        this.loadingPricing = false;
        return;
      }

      console.log('✓ Found matching policy term', { termId: term.id, termTitle: term.title });

      // Backend now automatically includes BASE coverages based on plan coverageConfigs and propertyValues
      // Frontend only needs to send user-selected optional coverage IDs
      const optionalCoverageIds = this.selectedOptionalCoverageIds;

      console.log('Calling calculatePricingPreview API', {
        planId: plan.id,
        termId: term.id,
        optionalCoverageIds,
        assetValuesCount: Object.keys(this.assetValues).length,
        selectedPricingDimensionIds: this.selectedPricingDimensionIds,
        note: 'BASE coverages will be automatically included by backend',
      });

      const result = await this.ratingMatrixService.calculatePricingPreview({
        planId: plan.id,
        planTitle: plan.title,
        termId: term.id,
        termTitle: term.title,
        termDuration: term.duration,
        coverageIds: optionalCoverageIds, // Only send optional coverage IDs; BASE coverages handled by backend
        propertyValues: this.assetValues,
        pricingDimensionIds: this.selectedPricingDimensionIds.length > 0 ? this.selectedPricingDimensionIds : undefined,
        dimensionValues: this.assetDimensionValues,
        asOfDate: this.asOfDate ?? this.getLocalTodayIsoDate(),
        distributorId: this.selectedDistributorId ?? undefined,
      });

      console.log('Pricing preview result', result);
      this.pricingPreview = result;

      const makeModel = this.getMakeModelTrimFromAssetValues();
      const makeModelKey = this.getMakeModelKeyFromAssetValues();
      if (makeModelKey && makeModelKey !== this.lastRefreshedMakeModelKey) {
        const existedBefore = makeModel
          ? await this.hasMakeModelEntry(makeModel.make, makeModel.model, makeModel.trim)
          : false;
        await this.refreshSelectedDimensionsAndMatrix();
        this.lastRefreshedMakeModelKey = makeModelKey;

        if (!existedBefore && makeModel) {
          const existsAfter = await this.hasMakeModelEntry(makeModel.make, makeModel.model, makeModel.trim);
          if (existsAfter) {
            this.toastService.show({
              type: 'success',
              title: 'Matrix updated',
              text: 'Rating matrix updated with new make/model/trim elements.',
            });
          }
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error('Error calculating pricing preview', err);
      this.toastService.show({
        type: 'error',
        title: 'Failed to calculate pricing',
        text: err?.message ?? 'Unable to calculate pricing preview. Please try again.',
      });
      this.pricingPreview = null;
    } finally {
      this.loadingPricing = false;
      console.log('Calculation completed, loadingPricing set to false');
    }
  }

  getTotalPrice(): number {
    if (!this.pricingPreview) return 0;
    return this.pricingPreview.price || 0;
  }

  private getSelectedTermElement(): Element | null {
    if (!this.selectedTermId) {
      return null;
    }

    return this.termElements.find(termElement => termElement.id === this.selectedTermId) ?? null;
  }

  private resolveSelectedPolicyTerm(policy: Policy.Model): Policy.PolicyTerm | null {
    const selectedTermElement = this.getSelectedTermElement();
    if (!selectedTermElement) {
      return null;
    }

    const normalizedTitle = selectedTermElement.title.toLowerCase().trim();
    let term: Policy.PolicyTerm | null =
      policy.terms.find(candidate => candidate.title === selectedTermElement.title) ?? null;

    if (!term) {
      term = policy.terms.find(candidate => candidate.title.toLowerCase().trim() === normalizedTitle) ?? null;
    }

    if (!term && Number.isFinite(selectedTermElement.sortOrder)) {
      term =
        policy.terms.find(
          candidate => Number.isFinite(candidate.order) && candidate.order === selectedTermElement.sortOrder
        ) ?? null;
    }

    return term ?? null;
  }

  /**
   * Helper for template: formats always-included (BASE) coverages as a comma-separated list.
   * We keep this logic in TS to avoid complex expressions/arrow functions in the Angular template.
   */
  getAlwaysIncludedCoverageTitles(): string {
    if (!this.alwaysIncludedCoverages || this.alwaysIncludedCoverages.length === 0) {
      return '';
    }
    return this.alwaysIncludedCoverages.map(c => c.title).join(', ');
  }

  onFilterElementsChange(dimensionIndex: number, selectedElements: readonly Element[] | Element | null) {
    const filterDim = this.selectedFilterDimensions[dimensionIndex];
    if (!filterDim) return;

    if (!selectedElements) {
      filterDim.selectedElementIds = [];
    } else if (Array.isArray(selectedElements)) {
      filterDim.selectedElementIds = [...selectedElements].map(e => e.id);
    } else if (selectedElements && typeof selectedElements === 'object' && 'id' in selectedElements) {
      filterDim.selectedElementIds = [(selectedElements as Element).id];
    } else {
      filterDim.selectedElementIds = [];
    }

    this.buildMatrix();
  }

  getSelectedFilterElements(dimensionIndex: number): Element[] {
    const filterDim = this.selectedFilterDimensions[dimensionIndex];
    if (!filterDim || !filterDim.selectedElementIds || filterDim.selectedElementIds.length === 0) {
      return filterDim?.elements || [];
    }
    const selectedIds = filterDim.selectedElementIds;
    return filterDim.elements.filter(e => selectedIds.includes(e.id));
  }

  compareElementsById = (a: Element | null, b: Element | null): boolean => {
    if (!a || !b) return false;
    return a.id === b.id;
  };

  getAssetEntries(): Array<{ key: string; value: unknown }> {
    const combinedValues: Record<string, unknown> = {
      ...this.assetValues,
      ...this.assetDimensionValues,
    };

    return Object.entries(combinedValues)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => {
        const displayValue = this.getAssetEntryDisplayValue(key, value);
        return { key, value: this.formatAssetEntryValue(displayValue) };
      })
      .filter(entry => entry.value !== '');
  }

  getAssetEntryLabel(key: string): string {
    if (!key) return '';

    const rawKey = key.trim();
    const dimensionIdFromPrefixedKey = rawKey.match(/^dim\s+([a-zA-Z0-9]+)$/i)?.[1] ?? null;
    const dimensionIdCandidate = dimensionIdFromPrefixedKey ?? rawKey;
    const dimension = this.dimensions.find(d => d.id === dimensionIdCandidate);
    if (dimension) {
      return dimension.title;
    }

    const withSpaces = rawKey
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .trim();

    return withSpaces
      .split(/\s+/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Get unique intersections grouped by cellId
   * Each intersection represents: element a + element b + element c = cell value
   */
  getUniqueIntersections(intersections: PricingPreview['intersections']): Array<{
    cellId: string | null;
    cellValue: number | null;
    formula?: IntersectionFormula;
    feeType: PricingPreview['intersections'][number]['feeType'];
    recipient: PricingPreview['intersections'][number]['recipient'];
    elements: Array<{
      dimensionId: string;
      dimensionTitle: string;
      elementId: string;
      elementTitle: string;
    }>;
  }> {
    // Group by cellId to get unique intersections
    const cellMap = new Map<
      string | null,
      {
        cellId: string | null;
        cellValue: number | null;
        formula?: IntersectionFormula;
        feeType: PricingPreview['intersections'][number]['feeType'];
        recipient: PricingPreview['intersections'][number]['recipient'];
        elements: Array<{
          dimensionId: string;
          dimensionTitle: string;
          elementId: string;
          elementTitle: string;
        }>;
      }
    >();

    for (const intersection of intersections) {
      const cellId = intersection.cellId ?? null;
      if (intersection.elements && intersection.elements.length > 0) {
        // Use cellId as key, or create a unique key from elements if no cellId
        const key = cellId ?? intersection.elements.map(e => `${e.dimensionId}:${e.elementId}`).join('|');
        if (!cellMap.has(key)) {
          const sortedElements = [...intersection.elements].sort((left, right) =>
            this.compareIntersectionElements(left, right)
          );

          cellMap.set(key, {
            cellId: cellId,
            cellValue: intersection.cellValue ?? null,
            formula: intersection.formula,
            feeType: intersection.feeType ?? null,
            recipient: intersection.recipient ?? null,
            elements: sortedElements,
          });
        }
      }
    }

    return Array.from(cellMap.values()).sort((left, right) => this.compareUniqueIntersections(left, right));
  }

  private compareUniqueIntersections(
    left: {
      cellId: string | null;
      cellValue: number | null;
      elements: Array<{
        dimensionId: string;
        dimensionTitle: string;
        elementId: string;
        elementTitle: string;
      }>;
    },
    right: {
      cellId: string | null;
      cellValue: number | null;
      elements: Array<{
        dimensionId: string;
        dimensionTitle: string;
        elementId: string;
        elementTitle: string;
      }>;
    }
  ): number {
    if (left.elements.length !== right.elements.length) {
      return left.elements.length - right.elements.length;
    }

    const length = Math.min(left.elements.length, right.elements.length);
    for (let index = 0; index < length; index += 1) {
      const comparison = this.compareIntersectionElements(left.elements[index], right.elements[index]);
      if (comparison !== 0) {
        return comparison;
      }
    }

    if ((left.cellId ?? '') !== (right.cellId ?? '')) {
      return (left.cellId ?? '').localeCompare(right.cellId ?? '');
    }

    return (left.cellValue ?? 0) - (right.cellValue ?? 0);
  }

  private compareIntersectionElements(
    left: {
      dimensionId: string;
      dimensionTitle: string;
      elementId: string;
      elementTitle: string;
    },
    right: {
      dimensionId: string;
      dimensionTitle: string;
      elementId: string;
      elementTitle: string;
    }
  ): number {
    const dimensionOrderComparison =
      this.getIntersectionDimensionOrder(left) - this.getIntersectionDimensionOrder(right);
    if (dimensionOrderComparison !== 0) {
      return dimensionOrderComparison;
    }

    const dimensionTitleComparison = left.dimensionTitle.localeCompare(right.dimensionTitle);
    if (dimensionTitleComparison !== 0) {
      return dimensionTitleComparison;
    }

    const elementTitleComparison = left.elementTitle.localeCompare(right.elementTitle);
    if (elementTitleComparison !== 0) {
      return elementTitleComparison;
    }

    return left.elementId.localeCompare(right.elementId);
  }

  private getIntersectionDimensionOrder(element: { dimensionId: string; dimensionTitle: string }): number {
    const dimensionIndex = this.dimensions.findIndex(d => d.id === element.dimensionId);
    if (dimensionIndex >= 0) {
      return dimensionIndex;
    }

    return Number.MAX_SAFE_INTEGER;
  }
}
